import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Receipt, Download, Edit, CheckCircle, RotateCcw, RefreshCw, Plus, Trash2, AlertCircle, FileText, Upload, Mail, MailCheck, Copy, Play, Search, X, Link2, ChevronDown } from 'lucide-react';
import { confirmAsync } from '@/lib/confirm';
import { mensajeDeError } from '@/lib/errores';
import { cuerpoDeFacturaNueva, totalDeLasLineas, motivoParaNoEmitir,
         periodoActual, resolverVariables, llevaVariables,
         descripcionDeLinea, puedeCambiarPrecio }
  from '@/lib/factura-nueva';
import { ClientSearchSelect } from '@/components/ClientSearchSelect';
import { ProductSearchSelect } from '@/components/ProductSearchSelect';
import { estadoDeEnvio } from '@/lib/envio';
import { resumenDeCobro } from '@/lib/cobros';
import {
  cuerpoDeDescarga, descripcionDelPeriodo, fallidas, mesDelRango,
  motivoParaNoDescargar, nombreDelFichero, rangoDelMes, rangoDelTrimestre,
  textoDelBoton,
} from '@/lib/exportar-facturas';
import { importeDeLineaConCliente, tipoDeLinea, formatoTipo } from '@/lib/impuestos';
import { DialogoRectificativa } from '@/components/DialogoRectificativa';
import { DialogoDevolucionManual } from '@/components/DialogoDevolucionManual';
import { DialogoCobro } from '@/components/DialogoCobro';
import { sinImporte } from '@/lib/cobros';
import { DialogoEnviarFactura } from '@/components/DialogoEnviarFactura';

export default function Invoices({ user }) {
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  // Filters
  const [filterRecurring, setFilterRecurring] = useState('all'); // all, recurring, unique
  const [filterStatus, setFilterStatus] = useState('all'); // all, draft, accepted, paid, pendientes, overdue, returned
  // Si le ha llegado al cliente por correo: all, sent, unsent. Angel,
  // 15-sep-2026: "para no enviarlas dos veces o saber que ya fue enviada".
  const [filterEmail, setFilterEmail] = useState('all');
  // La ficha del cliente enlaza aqui con ?search=<numero de factura>, asi que
  // el buscador arranca con lo que traiga la URL en vez de vacio.
  const [searchParams] = useSearchParams();
  const busquedaInicial = searchParams.get('search') || '';
  // La ficha del cliente enlaza con ?nueva=<id> para facturarle directamente.
  // Es un atajo a esta misma pantalla, no otro formulario: dos altas de
  // factura distintas acabarían diciendo cosas distintas.
  const clienteAFacturar = searchParams.get('nueva');
  const [searchTerm, setSearchTerm] = useState(busquedaInicial);
  // Rango de fecha de EMISION. El servidor ya lo entendia (`desde`/`hasta`),
  // pero esta pantalla no lo ofrecia, asi que no habia forma de acotar un
  // mes ni un trimestre -- que es lo que hay que mandarle a la gestoria
  // (Angel, 30-sep-2026).
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [descargando, setDescargando] = useState(false);
  // Valor retrasado: la busqueda se lanza cuando dejas de teclear, no en cada letra.
  const [debouncedSearch, setDebouncedSearch] = useState(busquedaInicial);
  
  // Dialogs
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  // Factura hecha a mano, sin presupuesto. Se reutiliza EL MISMO diálogo que
  // editar: tiene ya el buscador de clientes, el catálogo, las líneas con
  // descuento y los totales en vivo. Un segundo diálogo con el mismo trabajo
  // acaba divergiendo — pasó el 4-sep con el buscador de clientes.
  const [creandoFactura, setCreandoFactura] = useState(false);
  // Nace emitida y con número, así que se enseña qué se va a emitir antes de
  // hacerlo. Un fallo de tecleo aquí no se corrige: se rectifica.
  const [confirmarEmision, setConfirmarEmision] = useState(false);
  const [emitiendo, setEmitiendo] = useState(false);
  // El link de pago recién generado. Se enseña en su propio diálogo porque hay
  // que copiarlo: una factura cobrada con tarjeta no le sirve al cliente para
  // pagar, lo que necesita es la URL.
  const [linkGenerado, setLinkGenerado] = useState(null);
  const [generandoLink, setGenerandoLink] = useState(null);
  const [duplicando, setDuplicando] = useState(null);
  // La ficha del cliente elegido, para poder decir a quién se le va a emitir.
  const [clienteElegido, setClienteElegido] = useState(null);
  // Lo que el servidor contesta a "¿le puedo facturar a este?". Se pregunta al
  // ELEGIR el cliente, no al emitir: 329 de 2.037 clientes no pasan el candado
  // de datos fiscales, y enterarse después de teclear las líneas es tarde.
  const [estadoFacturacion, setEstadoFacturacion] = useState(null);
  const [billingAlertOpen, setBillingAlertOpen] = useState(false);
  const [billingAlertNote, setBillingAlertNote] = useState('');
  
  // PDF Import
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfPreviewData, setPdfPreviewData] = useState(null);
  const [processingPdf, setProcessingPdf] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);

  // Import repetitivas (XLSX Paygold/Xero)
  const recurringFileRef = useRef(null);
  const [recurringImporting, setRecurringImporting] = useState(false);
  const [recurringDialogOpen, setRecurringDialogOpen] = useState(false);
  const [recurringPreview, setRecurringPreview] = useState(null); // dry-run result
  const [recurringApplying, setRecurringApplying] = useState(false);
  const [recurringResult, setRecurringResult] = useState(null); // apply result
  const [recurringFile, setRecurringFile] = useState(null);
  
  // Excel Import
  const [excelDialogOpen, setExcelDialogOpen] = useState(false);
  const [excelConfigDialogOpen, setExcelConfigDialogOpen] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [excelPreviewData, setExcelPreviewData] = useState(null);
  const [excelRecurringConfig, setExcelRecurringConfig] = useState({
    start_date: '',
    recurrence_frequency: 'mensual',
    recurrence_interval: 1,
    end_date: ''
  });
  const [processingExcel, setProcessingExcel] = useState(false);
  const [creatingFromExcel, setCreatingFromExcel] = useState(false);
  
  // Edit form
  const [products, setProducts] = useState([]);
  const [editForm, setEditForm] = useState({
    customer_id: '',
    line_items: [],
    due_date: '',
    notes: '',
    status: 'draft',
    // Recurring fields
    is_recurring: false,
    start_date: '',
    recurrence_frequency: '',
    recurrence_interval: 1,
    end_date: '',
    // Paygold/Xero import fields (visibles para edicion)
    invoice_reference: '',
    platform_email: ''
  });

  // Cambio de filtros → volver a página 1
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [filterRecurring, filterStatus, filterEmail, debouncedSearch]);

  useEffect(() => {
    fetchInvoices();
  }, [filterRecurring, filterStatus, filterEmail, debouncedSearch, page, desde, hasta]);

  const fetchInvoices = async () => {
    setLoadingInvoices(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      if (desde) params.set('desde', desde);
      if (hasta) params.set('hasta', hasta);
      params.set('page_size', String(pageSize));
      if (filterRecurring === 'recurring') params.set('is_recurring', 'true');
      else if (filterRecurring === 'unique') params.set('is_recurring', 'false');
      if (filterStatus !== 'all') params.set('status', filterStatus);
      if (filterEmail === 'sent') params.set('email_enviado', 'true');
      else if (filterEmail === 'unsent') params.set('email_enviado', 'false');
      if (debouncedSearch && debouncedSearch.trim()) params.set('search', debouncedSearch.trim());

      const response = await axiosInstance.get(`/invoices?${params.toString()}`);
      // Nueva forma paginada: {items, total, page, page_size, pages}
      // Si por compatibilidad devuelve array, asumimos no paginado.
      if (Array.isArray(response.data)) {
        setInvoices(response.data);
        setTotalInvoices(response.data.length);
        setTotalPages(1);
      } else {
        setInvoices(response.data.items || []);
        setTotalInvoices(response.data.total || 0);
        setTotalPages(response.data.pages || 1);
      }
    } catch (error) {
      toast.error('Error al cargar facturas');
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await axiosInstance.put(`/invoices/${id}/status?status=${status}`);
      toast.success('Estado actualizado');
      fetchInvoices();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  /**
   * Marcar una factura como cobrada.
   *
   * Va aparte de `handleUpdateStatus` y pide confirmacion porque desde la
   * pantalla NO hay vuelta atras: los botones llevan de borrador a aceptada y
   * de aceptada a pagada, y de pagada no sale ninguno. Un clic de mas y la
   * factura consta cobrada sin que nadie haya pagado.
   *
   * El riesgo era real: el boton ponia "Pagada" en verde al lado de un estado
   * que decia "Aceptada", asi que parecia una etiqueta y no un boton.
   */
  /**
   * Apuntar el cobro de una factura.
   *
   * Abre el diálogo en vez de preguntar y marcar pagada entera: hay que poder
   * decir que han pagado una parte, cuánto y qué día (Angel, 24-sep-2026).
   */
  const handleMarcarPagada = (invoice) => setCobrando(invoice);

  /**
   * El banco ha devuelto el recibo de esta factura.
   *
   * Angel, 22-sep-2026: de momento se marca a mano; más adelante vendrá por
   * API desde la aplicación que lleva la recuperación. Deja de estar pagada,
   * y queda la marca de que se devolvió aunque luego la paguen.
   */
  const marcarReciboDevuelto = async () => {
    if (!devolviendoRecibo) return;
    setMarcandoDevuelto(true);
    try {
      const { data } = await axiosInstance.post(
        `/invoices/${devolviendoRecibo.id}/recibo-devuelto`,
        { fecha: fechaDevolucion || null, motivo: motivoDevolucion.trim() || null });
      toast.success(data.message);
      setDevolviendoRecibo(null);
      fetchInvoices();
    } catch (error) {
      toast.error(mensajeDeError(error));
    } finally {
      setMarcandoDevuelto(false);
    }
  };

  const handleToggleSelection = (invoiceId) => {
    if (selectedInvoices.includes(invoiceId)) {
      setSelectedInvoices(selectedInvoices.filter(id => id !== invoiceId));
    } else {
      setSelectedInvoices([...selectedInvoices, invoiceId]);
    }
  };

  const handleApproveSelected = async () => {
    if (selectedInvoices.length === 0) {
      toast.error('No hay facturas seleccionadas');
      return;
    }

    try {
      const response = await axiosInstance.post('/invoices/approve-multiple', selectedInvoices);
      const approved = response.data.approved_invoices.length;
      const generated = response.data.generated_invoices.length;
      // Un unico toast en vez de 2: menos ruido, mensaje mas denso
      toast.success(
        generated > 0
          ? `${approved} aprobadas · ${generated} recurrentes generadas`
          : `${approved} facturas aprobadas`
      );

      setSelectedInvoices([]);
      fetchInvoices();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Error al aprobar facturas');
    }
  };


  /**
   * Bajarse las facturas del filtro (o las marcadas) en un ZIP.
   *
   * Angel, 30-sep-2026: se las manda a la gestoria cada trimestre, y prefiere
   * sacarlas mes a mes -- unas 350 cada vez.
   *
   * El ZIP lo monta el servidor, que es quien tiene los PDF. Aqui solo se
   * pide y se guarda.
   */
  const handleDescargarZip = async () => {
    const cuantas = { marcadas: selectedInvoices, total: totalInvoices };
    const pega = motivoParaNoDescargar(cuantas);
    if (pega) {
      toast.error(pega);
      return;
    }

    setDescargando(true);
    // Un mes son ~350 PDF y unos segundos: sin este aviso parece colgado.
    toast.info('Preparando el ZIP…');
    try {
      const respuesta = await axiosInstance.post(
        '/invoices/export/zip',
        cuerpoDeDescarga({
          marcadas: selectedInvoices,
          filtros: { desde, hasta, status: filterStatus, search: debouncedSearch,
                     recurring: filterRecurring, email: filterEmail },
        }),
        { responseType: 'blob' },
      );

      const url = URL.createObjectURL(respuesta.data);
      const enlace = document.createElement('a');
      enlace.href = url;
      enlace.download = nombreDelFichero(respuesta.headers);
      enlace.click();
      // Sin esto el blob se queda en memoria hasta recargar la pagina, y
      // tres descargas de 350 facturas se notan.
      URL.revokeObjectURL(url);

      const fallos = fallidas(respuesta.headers);
      if (fallos > 0) {
        // No se puede mandar a la gestoria un ZIP al que le faltan facturas
        // sin saberlo. El detalle va dentro, en _FALTAN_ESTAS.txt.
        toast.error(
          `Descargado, pero ${fallos} factura(s) no se han podido generar. `
          + 'Mira _FALTAN_ESTAS.txt dentro del ZIP antes de mandarlo.',
          { duration: 12000 });
      } else {
        toast.success('ZIP descargado');
      }
    } catch (error) {
      // El error viaja como blob por el `responseType`, asi que hay que
      // leerlo para poder enseñar lo que dice el servidor.
      let mensaje = mensajeDeError(error);
      try {
        const texto = await error.response?.data?.text?.();
        if (texto) mensaje = JSON.parse(texto).detail || mensaje;
      } catch (_) { /* si no se puede leer, vale el generico */ }
      toast.error(mensaje);
    } finally {
      setDescargando(false);
    }
  };

  /** Pone el rango de un mes entero: es como Angel saca las facturas. */
  const ponerMes = (mes) => {
    const rango = rangoDelMes(mes);
    if (!rango) { setDesde(''); setHasta(''); return; }
    setDesde(rango.desde);
    setHasta(rango.hasta);
    setPage(1);
  };

  const ponerTrimestre = (trimestre) => {
    const anyo = Number((desde || new Date().toISOString()).slice(0, 4));
    const rango = rangoDelTrimestre(anyo, trimestre);
    if (!rango) return;
    setDesde(rango.desde);
    setHasta(rango.hasta);
    setPage(1);
  };

  // Select/Deselect all invoices
  const handleSelectAll = () => {
    if (selectedInvoices.length === invoices.length && invoices.length > 0) {
      // If all are selected, deselect all
      setSelectedInvoices([]);
    } else {
      // Select all invoices
      setSelectedInvoices(invoices.map(inv => inv.id));
    }
  };

  // Ejecutar cron de facturas repetitivas manualmente (para pruebas)
  const handleRunRecurringCron = async () => {
    try {
      toast.info('Ejecutando generación de facturas repetitivas...');
      const response = await axiosInstance.post('/invoices/generate-recurring');
      
      if (response.data.created_count > 0) {
        toast.success(`✅ ${response.data.created_count} factura(s) generada(s)`);
      } else {
        toast.info('No hay facturas repetitivas pendientes de generar');
      }
      
      if (response.data.skipped_count > 0) {
        toast.warning(`⚠️ ${response.data.skipped_count} plantilla(s) omitida(s)`);
      }
      
      if (response.data.errors?.length > 0) {
        toast.error(`❌ ${response.data.errors.length} error(es)`);
      }
      
      fetchInvoices();
    } catch (error) {
      console.error('Error running recurring cron:', error);
      toast.error('Error al ejecutar el cron de facturas repetitivas');
    }
  };

  // Import repetitivas desde XLSX (Paygold/Xero)
  const handleRecurringPickFile = () => {
    recurringFileRef.current?.click();
  };

  const handleRecurringFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // reset para re-seleccionar el mismo archivo

    setRecurringFile(file);
    setRecurringPreview(null);
    setRecurringResult(null);
    setRecurringDialogOpen(true);
    setRecurringImporting(true);
    try {
      // Paso 1: dry-run para preview
      const formData = new FormData();
      formData.append('file', file);
      const response = await axiosInstance.post(
        '/invoices/import-recurring?apply=false',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setRecurringPreview(response.data);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Error analizando el XLSX');
      setRecurringPreview({ error: detail || 'Error desconocido' });
    } finally {
      setRecurringImporting(false);
    }
  };

  const handleRecurringApply = async () => {
    if (!recurringFile) return;
    setRecurringApplying(true);
    try {
      const formData = new FormData();
      formData.append('file', recurringFile);
      const response = await axiosInstance.post(
        '/invoices/import-recurring?apply=true',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setRecurringResult(response.data);
      const d = response.data;
      // Un import puede crear 0 y aun asi cambiar cientos de plantillas: el
      // resumen tiene que contar las tres cosas o parece que no ha hecho nada.
      toast.success(
        `Repetitivas: ${d.templates_created} creadas, ${d.templates_updated ?? 0} actualizadas, ` +
        `${d.templates_deleted ?? 0} dadas de baja`
      );
      fetchInvoices();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Error aplicando el import');
      setRecurringResult({ error: detail || 'Error desconocido' });
    } finally {
      setRecurringApplying(false);
    }
  };

  const closeRecurringDialog = () => {
    setRecurringDialogOpen(false);
    setRecurringFile(null);
    setRecurringPreview(null);
    setRecurringResult(null);
  };

  // Delete selected invoices
  const handleDeleteSelected = async () => {
    if (selectedInvoices.length === 0) {
      toast.error('No hay facturas seleccionadas');
      return;
    }

    if (!(await confirmAsync(`¿Estás seguro de que quieres eliminar ${selectedInvoices.length} facturas?`))) {
      return;
    }

    try {
      let deletedCount = 0;
      let errorCount = 0;

      for (const invoiceId of selectedInvoices) {
        try {
          await axiosInstance.delete(`/invoices/${invoiceId}`);
          deletedCount++;
        } catch (error) {
          errorCount++;
        }
      }

      if (deletedCount > 0) {
        toast.success(`${deletedCount} facturas eliminadas`);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} facturas no se pudieron eliminar`);
      }
      
      setSelectedInvoices([]);
      fetchInvoices();
    } catch (error) {
      toast.error('Error al eliminar facturas');
    }
  };


  const handlePdfFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.pdf')) {
      toast.error('Por favor, selecciona un archivo PDF');
      return;
    }
    
    setPdfFile(file);
    setProcessingPdf(true);
    setPdfPreviewData(null);
    
    // Process PDF - use multiple endpoint for PDF files
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      // Try multiple invoices endpoint first
      const response = await axiosInstance.post('/invoices/process-multiple-pdfs', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.total_pages > 1) {
        // Multiple invoices detected
        const { invoices_processed, invoices_failed, total_pages } = response.data;
        
        setPdfPreviewData(response.data);
        
        if (invoices_failed > 0) {
          toast.warning(
            `${invoices_processed} facturas procesadas de ${total_pages} páginas. ${invoices_failed} requieren revisión.`,
            { duration: 5000 }
          );
        } else {
          toast.success(
            `¡Éxito! ${invoices_processed} facturas procesadas de ${total_pages} páginas`,
            { duration: 4000 }
          );
        }
      } else {
        // Single invoice - use old format for compatibility
        const singleInvoice = response.data.invoices[0];
        if (singleInvoice && singleInvoice.success) {
          setPdfPreviewData({
            success: true,
            client: singleInvoice.client,
            products: singleInvoice.products,
            summary: singleInvoice.summary
          });
          toast.success('PDF procesado correctamente');
          
          // Show warnings
          const warnings = singleInvoice.products
            .filter(p => p.price_warning)
            .map(p => p.price_warning);
          
          if (warnings.length > 0) {
            warnings.forEach(w => toast.warning(w, { duration: 5000 }));
          }
        } else {
          throw new Error(singleInvoice?.error || 'Error procesando PDF');
        }
      }
      
    } catch (error) {
      const errorMsg = mensajeDeError(error, 'Error al procesar el PDF');
      toast.error(errorMsg);
      setPdfFile(null);
    } finally {
      setProcessingPdf(false);
    }
  };

  const handleCreateInvoiceFromPdf = async () => {
    if (!pdfPreviewData) {
      toast.error('No hay datos para crear la factura');
      return;
    }

    // Check if multiple invoices
    const isMultiple = pdfPreviewData.total_pages > 1;
    const confirmMessage = isMultiple 
      ? `¿Confirmar creación de ${pdfPreviewData.invoices_processed} facturas borrador recurrentes?`
      : '¿Confirmar creación de factura borrador recurrente?';

    if (!(await confirmAsync(confirmMessage))) {
      return;
    }

    setCreatingInvoice(true);

    try {
      if (isMultiple) {
        // Create multiple invoices
        let created = 0;
        let failed = 0;

        for (const invoice of pdfPreviewData.invoices) {
          if (!invoice.success) continue;

          try {
            await axiosInstance.post('/invoices/create-from-pdf', {
              customer_id: invoice.client.id,
              products: invoice.products
            });
            created++;
          } catch (error) {
            console.error(`Error creando factura página ${invoice.page}:`, error);
            failed++;
          }
        }

        if (created > 0) {
          toast.success(`${created} facturas creadas correctamente${failed > 0 ? `. ${failed} fallidas` : ''}`, {
            duration: 5000
          });
        } else {
          toast.error('No se pudo crear ninguna factura');
        }
      } else {
        // Single invoice
        const response = await axiosInstance.post('/invoices/create-from-pdf', {
          customer_id: pdfPreviewData.client.id,
          products: pdfPreviewData.products
        });
        toast.success(response.data.message);
      }

      setPdfDialogOpen(false);
      setPdfFile(null);
      setPdfPreviewData(null);
      fetchInvoices();
    } catch (error) {
      const errorMsg = mensajeDeError(error, 'Error al crear factura');
      toast.error(errorMsg);
    } finally {
      setCreatingInvoice(false);
    }
  };


  // Excel Import Functions
  const handleExcelFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate recurring config before processing
    if (!excelRecurringConfig.start_date) {
      toast.error('Debe configurar la fecha de inicio primero');
      return;
    }

    setExcelFile(file);
    setProcessingExcel(true);
    setExcelPreviewData(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('start_date', excelRecurringConfig.start_date);
      formData.append('recurrence_frequency', excelRecurringConfig.recurrence_frequency);
      formData.append('recurrence_interval', excelRecurringConfig.recurrence_interval.toString());
      if (excelRecurringConfig.end_date) {
        formData.append('end_date', excelRecurringConfig.end_date);
      }

      const response = await axiosInstance.post('/invoices/process-excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setExcelPreviewData(response.data);

      if (response.data.errors && response.data.errors.length > 0) {
        toast.warning(`Archivo procesado con ${response.data.errors.length} errores`);
      } else {
        toast.success('Archivo procesado correctamente');
      }
    } catch (error) {
      const errorMsg = mensajeDeError(error, 'Error al procesar el archivo Excel');
      toast.error(errorMsg);
      setExcelPreviewData(null);
    } finally {
      setProcessingExcel(false);
    }
  };

  const handleCreateInvoicesFromExcel = async () => {
    if (!excelPreviewData || !excelPreviewData.invoices_preview) return;

    const validInvoices = excelPreviewData.invoices_preview.filter(inv => inv.valid);
    
    if (validInvoices.length === 0) {
      toast.error('No hay facturas válidas para crear');
      return;
    }

    if (!(await confirmAsync(`¿Crear ${validInvoices.length} facturas recurrentes en estado aceptado?`))) {
      return;
    }

    setCreatingFromExcel(true);

    try {
      // Include recurring configuration along with invoices
      const payload = {
        invoices: validInvoices,
        recurring_config: excelRecurringConfig
      };
      
      const response = await axiosInstance.post('/invoices/create-from-excel', payload);
      
      toast.success(response.data.message);
      
      setExcelDialogOpen(false);
      setExcelFile(null);
      setExcelPreviewData(null);
      fetchInvoices();
    } catch (error) {
      console.error('Error creating invoices:', error.response?.data);
      
      let errorMsg = 'Error al crear facturas';
      
      // Handle validation errors (422)
      if (error.response?.status === 422) {
        const detail = error.response?.data?.detail;
        if (Array.isArray(detail)) {
          // Pydantic validation errors
          errorMsg = detail.map(err => `${err.loc?.join('.')}: ${err.msg}`).join(', ');
        } else if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (detail && typeof detail === 'object') {
          errorMsg = JSON.stringify(detail);
        }
      } else {
        errorMsg = mensajeDeError(error, 'Error al crear facturas');
      }
      
      toast.error(errorMsg);
    } finally {
      setCreatingFromExcel(false);
    }
  };

  // La rectificativa se emite desde su propio diálogo: hay que elegir qué
  // conceptos se devuelven y decir por qué. Antes era un `¿Estás seguro?` y
  // se emitía la factura entera con un número inventado.
  const [rectificando, setRectificando] = useState(null);
  // Apuntar un cobro: entero o a plazos. Antes era un `¿Estás seguro?`
  // que la dejaba pagada entera, y cobrar la mitad no se podía contar.
  const [cobrando, setCobrando] = useState(null);
  // Devolución de una factura que NO está aquí: la original se emitió con la
  // aplicación anterior, así que no hay factura de la que partir y los
  // conceptos se escriben. Puerta aparte a propósito: emitir en negativo no
  // puede colgar del mismo botón con el que se factura todos los días.
  const [devolviendoAMano, setDevolviendoAMano] = useState(false);
  // Recibo devuelto por el banco: la factura sobre la que se está marcando,
  // con su fecha y su motivo.
  const [devolviendoRecibo, setDevolviendoRecibo] = useState(null);
  const [fechaDevolucion, setFechaDevolucion] = useState('');
  const [motivoDevolucion, setMotivoDevolucion] = useState('');
  const [marcandoDevuelto, setMarcandoDevuelto] = useState(false);


  const handleDeleteInvoice = async (invoiceId, invoiceNumber, customerName) => {
    const displayName = invoiceNumber || `factura de ${customerName}`;
    
    if (!(await confirmAsync(`⚠️ ¿Estás seguro de ELIMINAR PERMANENTEMENTE la ${displayName}?\n\nEsta acción NO se puede deshacer.`))) {
      return;
    }

    try {
      const response = await axiosInstance.delete(`/invoices/${invoiceId}`);
      toast.success(response.data.message || 'Factura eliminada correctamente');
      fetchInvoices();
      
      // Clear selection if deleted invoice was selected
      setSelectedInvoices(selectedInvoices.filter(id => id !== invoiceId));
    } catch (error) {
      const errorMsg = mensajeDeError(error, 'Error al eliminar la factura');
      toast.error(errorMsg);
    }
  };


  const handleConvertProformaToDefinitive = async (proformaId, proformaNumber) => {
    if (!(await confirmAsync(`¿Convertir la proforma ${proformaNumber} en factura definitiva?`))) {
      return;
    }
    
    try {
      const response = await axiosInstance.post(`/invoices/${proformaId}/convert-to-definitive`);
      toast.success(response.data.message);
      fetchInvoices();
    } catch (error) {
      toast.error('Error: ' + mensajeDeError(error));
    }
  };


  // El envio se hace desde su propio dialogo, que trae puesta la direccion de
  // la ficha. Antes era `prompt('Introduce el email del cliente:')`: teclear
  // la direccion a mano cada vez, sin ver de que factura se trata.
  const [enviando, setEnviando] = useState(null);

  const handleDownloadPDF = async (invoiceId, invoiceNumber) => {
    try {
      const response = await axiosInstance.get(`/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      
      // Crear un blob y descargarlo
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `factura_${invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF descargado correctamente');
    } catch (error) {
      toast.error('Error al descargar PDF: ' + mensajeDeError(error));
    }
  };

  const FORMULARIO_VACIO = {
    customer_id: '', line_items: [], due_date: '', notes: '', status: 'draft',
    is_recurring: false, start_date: '', recurrence_frequency: '',
    recurrence_interval: 1, end_date: '', payment_method: '',
    invoice_reference: '', platform_email: '',
    // Para el cliente (sale impresa) y de qué mes es el servicio. `notes` es
    // interna: son dos campos a propósito.
    observaciones: '', periodo: ''
  };

  /** Pregunta al servidor si a esta ficha se le puede emitir una suelta. */
  const comprobarSiSePuedeFacturar = async (customerId) => {
    if (!customerId) {
      setEstadoFacturacion(null);
      return;
    }
    try {
      // `suelta=true`: sin contrato detrás no hace falta IBAN. Sin eso, el
      // aviso diría que falta el IBAN a clientes que el servidor factura sin
      // problema, y un aviso que no se cumple deja de leerse.
      const { data } = await axiosInstance.get(
        `/leads/${customerId}/billing-status?suelta=true`);
      setEstadoFacturacion(data);
    } catch (error) {
      // Si no se puede preguntar, no se afirma nada: decide el servidor al
      // emitir, que para eso lleva el mismo candado.
      setEstadoFacturacion(null);
    }
  };

  const abrirNuevaFactura = async (customerId = null) => {
    try {
      const productsRes = await axiosInstance.get('/products');
      setProducts(productsRes.data);
      setEditingInvoice(null);
      // Vence el mismo día que se emite, y se puede cambiar. Antes eran 30
      // días: un plazo inventado que nadie había pedido y que en una factura
      // que se cobra en el acto no significa nada.
      // El periodo arranca en el mes de hoy: es lo que resuelve {mes}/{año}
      // en las líneas, y lo normal es cambiarlo al mes anterior cuando lo
      // que se factura es el servicio ya prestado.
      setEditForm({ ...FORMULARIO_VACIO, due_date: paraInputFecha(new Date()),
                    periodo: periodoActual(), customer_id: customerId || '' });
      setClienteElegido(null);
      setEstadoFacturacion(null);
      setCreandoFactura(true);
      setEditDialogOpen(true);
      // Viniendo de la ficha del cliente ya sabemos a quién: se comprueba
      // aquí, porque el selector no dispara `onValueChange` cuando el valor
      // le llega puesto desde fuera. Por lo mismo hay que traerse la ficha
      // para saber cómo paga.
      if (customerId) {
        await comprobarSiSePuedeFacturar(customerId);
        try {
          const { data } = await axiosInstance.get(`/customers/${customerId}`);
          setClienteElegido(data);
          if (data?.payment_method) {
            setEditForm((f) => ({ ...f, payment_method: data.payment_method }));
          }
        } catch (e) {
          // Sin la ficha se sigue pudiendo facturar: solo hay que elegir a
          // mano la forma de pago, que es obligatoria de todas formas.
        }
      }
    } catch (error) {
      toast.error(mensajeDeError(error, 'No se pudo abrir el formulario'));
    }
  };

  const handleOpenEdit = async (invoice) => {
    setCreandoFactura(false);
    // Sin esto, el aviso de "no se le puede facturar" de un borrador seria el
    // del ultimo cliente que se eligio en otra factura.
    setEstadoFacturacion(null);
    try {
      // Ya no se descarga ninguna lista de clientes: la busca el propio
      // selector contra el servidor segun escribes.
      //
      // Antes se pedian `?page=1&page_size=100` sobre 2.044 clientes activos,
      // ordenados por fecha de alta descendente. O sea: se ofrecian los 100
      // ultimos dados de alta y los otros 1.944 no existian para este
      // formulario, que es justo donde hace falta cambiar el cliente de una
      // factura mal importada.
      const productsRes = await axiosInstance.get('/products');

      // Del cliente de la factura si hace falta la ficha entera, para poder
      // pintar quien es antes de que nadie busque nada.
      let clienteDeLaFactura = null;
      if (invoice.customer_id) {
        try {
          const { data } = await axiosInstance.get(`/customers/${invoice.customer_id}`);
          clienteDeLaFactura = data;
        } catch (err) {
          // Una ficha borrada no puede impedir abrir la factura para
          // arreglarla: precisamente por eso se abre.
          console.error('No se pudo cargar el cliente de la factura:', err);
        }
      }

      setProducts(productsRes.data);
      // La ficha del cliente decide el impuesto de la vista previa de cada
      // linea. Se cargaba y no se guardaba, asi que al corregir la factura de
      // un canario la pantalla calculaba al 21% (el servidor no: el recalcula
      // con su regimen). Pantalla y papel tienen que decir lo mismo.
      setClienteElegido(clienteDeLaFactura);
      setEditingInvoice(invoice);
      
      // Transformar items si vienen del formato de presupuesto
      // Usar items si line_items está vacío
      const items = (invoice.line_items && invoice.line_items.length > 0) ? invoice.line_items : (invoice.items || []);
      console.log('🔍 Items encontrados:', items);
      console.log('🔍 Items length:', items.length);
      const transformedItems = items.map(item => {
        // Normalizar campos (soporte para formato antiguo de Excel: net/gross y nuevo: subtotal/total)
        const discount = item.discount ?? item.discount_percentage ?? 0;
        const subtotal = item.subtotal ?? item.net ?? (item.unit_price * item.quantity * (1 - discount/100));
        const iva_amount = item.iva_amount ?? (subtotal * 0.21);
        const total = item.total ?? item.gross ?? (subtotal + iva_amount);
        
        return {
          description: item.description || item.product_name || 'Sin descripción',
          unit_price: item.unit_price || 0,
          quantity: item.quantity || 1,
          discount: discount,
          subtotal: subtotal,
          iva_amount: iva_amount,
          total: total,
          product_id: item.product_id,
          product_ref: item.product_ref,
          product_type: item.product_type
        };
      });
      
      setEditForm({
        customer_id: invoice.customer_id,
        line_items: transformedItems,
        due_date: paraInputFecha(invoice.due_date),
        notes: invoice.notes || '',
        observaciones: invoice.observaciones || '',
        // Sin `periodo` guardado manda la fecha de expedición, que es
        // justo lo que usa el servidor al guardar. Si aquí quedara vacío, la
        // vista previa diría "{mes}" y el papel saldría con el mes resuelto.
        periodo: invoice.periodo
          || (invoice.issue_date ? periodoActual(new Date(invoice.issue_date)) : ''),
        status: invoice.status || 'draft',
        // Recurring fields
        is_recurring: invoice.is_recurring || false,
        start_date: paraInputFecha(invoice.start_date),
        recurrence_frequency: invoice.recurrence_frequency || '',
        recurrence_interval: invoice.recurrence_interval || 1,
        end_date: paraInputFecha(invoice.end_date),
        payment_method: invoice.payment_method || '',
        invoice_reference: invoice.invoice_reference || '',
        platform_email: invoice.platform_email || ''
      });
      
      // El aviso de facturación del cliente. Sale de la ficha que se acaba de
      // cargar; antes se buscaba en la lista de 100 y por eso no aparecía en
      // la inmensa mayoría de las facturas.
      if (clienteDeLaFactura?.show_billing_alert && clienteDeLaFactura?.billing_note) {
        setBillingAlertNote(clienteDeLaFactura.billing_note);
        setBillingAlertOpen(true);
      } else {
        setEditDialogOpen(true);
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    }
  };

  /**
   * Los importes de UNA linea para la vista previa, con el impuesto que le
   * toque: el propio de la linea si lo tiene (heredado del producto), y si
   * no el del cliente. Antes era `subtotal * 0.21` escrito a mano, que a un
   * canario le ensenaba un 21% que el servidor luego no cobraba.
   */
  const conImportes = (linea) => {
    const { base, cuota, total } = importeDeLineaConCliente(linea, clienteElegido);
    return { ...linea, subtotal: base, iva_amount: cuota, total };
  };

  const handleAddProduct = (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;

    const newItem = conImportes({
      product_id: product.id,
      // Nombre y, debajo, la descripcion del catalogo con sus saltos de
      // linea y sus {mes}/{año}. Antes solo entraba el nombre.
      description: descripcionDeLinea(product),
      quantity: 1,
      unit_price: product.price || 0,
      discount: 0,
      // Se copia a la linea, como en los presupuestos: decide si el precio
      // se puede tocar aqui. Queda congelado en la factura.
      allow_custom_line: Boolean(product.allow_custom_line),
      // El impuesto PROPIO del producto, si lo tiene. Manda sobre el del
      // cliente (comisiones exentas, al 0% aunque el cliente sea de Madrid).
      // null = el del cliente. El servidor hace la misma herencia por su
      // cuenta; esto es para que la vista previa diga lo mismo.
      tax_rate: product.tax_rate === null || product.tax_rate === undefined ? null : product.tax_rate,
      tax_note: product.tax_note || null,
    });

    setEditForm({
      ...editForm,
      line_items: [...editForm.line_items, newItem]
    });
  };

  const handleRemoveItem = (index) => {
    const newItems = editForm.line_items.filter((_, i) => i !== index);
    setEditForm({ ...editForm, line_items: newItems });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...editForm.line_items];
    
    // Para campos de texto (description), mantener como string
    if (field === 'description') {
      newItems[index][field] = value;
    } else if (field === 'tax_rate') {
      // Vacio es "el del cliente", no 0. No son lo mismo: 0 es "esta linea
      // no lleva impuesto".
      newItems[index][field] = value === '' || value === null ? null : (parseFloat(value) || 0);
    } else {
      newItems[index][field] = parseFloat(value) || 0;
    }

    // El mismo calculo que el servidor, con el impuesto de ESTA linea.
    newItems[index] = conImportes(newItems[index]);
    
    setEditForm({ ...editForm, line_items: newItems });
  };

  const totalDelFormulario = () => totalDeLasLineas(editForm.line_items);

  /** Pide un link de pago para una factura ya emitida. */
  const generarLinkDePago = async (invoice) => {
    setGenerandoLink(invoice.id);
    try {
      const { data } = await axiosInstance.post(
        `/invoices/${invoice.id}/payment-link`);
      setLinkGenerado({ ...data, numero: invoice.invoice_number });
      fetchInvoices();
    } catch (error) {
      // El caso normal: ya está cobrada (409) o no hay pasarela (503). Los dos
      // los explica el servidor mejor de lo que podría hacerlo esta pantalla.
      toast.error(mensajeDeError(error, 'No se pudo generar el link'));
    } finally {
      setGenerandoLink(null);
    }
  };

  /**
   * Volver a la lista entera tras emitir, con lo recién hecho a la vista.
   *
   * Myriam, 31-oct-2026: la búsqueda anterior se quedaba puesta y lo que acababa
   * de hacer no salía. Si cambia algo ya recarga el efecto de los filtros: llamar
   * aquí además lanzaría una petición con la búsqueda vieja, que podría llegar
   * la última.
   */
  const verLoRecienHecho = () => {
    const recargaSola = Boolean(debouncedSearch) || page !== 1;
    setSearchTerm('');
    setDebouncedSearch('');
    setPage(1);
    if (!recargaSola) fetchInvoices();
  };

  const emitirFacturaNueva = async () => {
    setEmitiendo(true);
    try {
      // Los importes NO viajan: los calcula el servidor desde las líneas, con
      // el impuesto que le toque a ESE cliente. Mandarlos desde aquí permitía
      // guardar una factura cuyo total no cuadraba con sus propias líneas.
      const { data } = await axiosInstance.post(
        '/invoices', cuerpoDeFacturaNueva(editForm));

      toast.success(`Factura ${data.invoice_number} emitida`, {
        description: 'Se la puedes enviar con el botón del sobre.'
      });
      setConfirmarEmision(false);
      setEditDialogOpen(false);
      setCreandoFactura(false);

      if (data.payment_link) {
        setLinkGenerado({ ...data.payment_link, numero: data.invoice_number });
      } else if (editForm.payment_method === 'link') {
        // La factura está emitida y con número: eso no se toca. Lo que falta
        // es el link, y se puede volver a pedir desde la lista.
        toast.warning('La factura está emitida, pero no se pudo crear el link', {
          description: 'Genéralo desde la lista con el botón del link.'
        });
      }
      verLoRecienHecho();
    } catch (error) {
      // El caso frecuente: al cliente le faltan datos fiscales. El servidor
      // dice cuáles, y eso es lo que hay que leer — no un "error al crear".
      toast.error(mensajeDeError(error, 'No se pudo emitir la factura'));
      setConfirmarEmision(false);
    } finally {
      setEmitiendo(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editForm.customer_id) {
      toast.error('Selecciona un cliente');
      return;
    }
    
    if (editForm.line_items.length === 0) {
      toast.error('Agrega al menos un item');
      return;
    }

    // La forma de pago la elige quien factura, siempre. Es lo que el PDF le
    // PIDE al cliente -- "haz una transferencia" o "no hagas nada, se te
    // gira" --, así que heredarla en silencio era decidir por él. En un
    // borrador también: al cambiarle el cliente se vacía si el nuevo no tiene
    // (el 3-nov-2026 no había ningún borrador suelto, así que no frena a nadie).
    if (comoUnAlta && !editForm.payment_method) {
      toast.error('Elige cómo se cobra esta factura');
      return;
    }

    // Un alta no guarda: EMITE. Se enseña antes qué se va a emitir y por
    // cuánto, porque a partir de ahí la factura tiene número y lo único que
    // arregla un error es una rectificativa.
    if (creandoFactura) {
      setConfirmarEmision(true);
      return;
    }

    // Validate recurring fields if is_recurring is checked
    if (editForm.is_recurring) {
      if (!editForm.start_date) {
        toast.error('La fecha de inicio es obligatoria para facturas repetitivas');
        return;
      }
      if (!editForm.recurrence_frequency) {
        toast.error('La cadencia es obligatoria para facturas repetitivas');
        return;
      }
    }

    try {
      // subtotal / iva_amount / total NO se envian a proposito: los recalcula
      // el servidor desde las lineas. Mandarlos permitia guardar una factura
      // cuyo total no cuadraba con sus propias lineas.
      const updateData = {
        customer_id: editForm.customer_id,
        line_items: editForm.line_items,
        due_date: editForm.due_date,
        notes: editForm.notes,
        observaciones: editForm.observaciones,
        type: editingInvoice.type,
        payment_method: editForm.payment_method,  // Forma de pago
        // Paygold/Xero campos editables
        invoice_reference: editForm.invoice_reference || null,
        platform_email: editForm.platform_email || null,
        // Recurring fields - only include if recurring
        is_recurring: editForm.is_recurring
      };

      // El estado solo viaja cuando el formulario lo ofrece (borradores y
      // plantillas). En una factura emitida se corrigen los datos, no el
      // estado: mandarlo aqui era la via para devolver una pagada a borrador.
      if (!editandoFacturaEmitida) {
        updateData.status = editForm.status;
      }

      // Only add recurring fields if the invoice is recurring
      if (editForm.is_recurring) {
        // Only add fields that have values (don't send null to overwrite existing data)
        if (editForm.start_date) {
          updateData.start_date = editForm.start_date;
        }
        if (editForm.recurrence_frequency) {
          updateData.recurrence_frequency = editForm.recurrence_frequency;
        }
        if (editForm.recurrence_interval) {
          updateData.recurrence_interval = editForm.recurrence_interval;
        }
        if (editForm.end_date) {
          updateData.end_date = editForm.end_date;
        }
      }

      // Optimistic update: PUT devuelve la factura actualizada; la reflejamos
      // en el state sin esperar al re-fetch para que la lista cambie al instante.
      const { data: updated } = await axiosInstance.put(`/invoices/${editingInvoice.id}`, updateData);

      // Marcar repetitiva una factura YA EMITIDA no la convierte: deja la
      // factura como está y crea una plantilla aparte. El servidor lo dice
      // con `plantilla_id`, y hay que contarlo: si no, parece que la casilla
      // no se ha guardado (Angel, 7-oct-2026).
      if (updated.plantilla_id) {
        toast.success(updated.message, { duration: 9000 });
      } else {
        setInvoices((prev) => prev.map((inv) => (inv.id === updated.id ? { ...inv, ...updated } : inv)));
        toast.success('Factura actualizada correctamente');
      }
      setEditDialogOpen(false);
      // Safety re-fetch por si el backend calcula campos derivados (asiento contable, etc.)
      fetchInvoices();
    } catch (error) {
      toast.error(mensajeDeError(error, 'Error al actualizar factura'));
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { label: 'Borrador', class: 'bg-gray-100 text-gray-800' },
      accepted: { label: 'Aceptada', class: 'bg-blue-100 text-blue-800' },
      paid: { label: 'Pagada', class: 'bg-green-100 text-green-800' },
      overdue: { label: 'Vencida', class: 'bg-red-100 text-red-800' },
      returned: { label: 'Devuelta', class: 'bg-purple-100 text-purple-800' },
      // El banco devolvió el recibo: estaba pagada y ha dejado de estarlo.
      // En ámbar y no en rojo: "Vencida" es que nadie la ha pagado a tiempo;
      // esto es que se intentó cobrar y volvió. Son dos cosas distintas.
      recibo_devuelto: { label: 'Recibo devuelto', class: 'bg-amber-100 text-amber-900' },
      // Cobrada a medias. En azul claro y no en verde: verde es "cobrada", y
      // esta todavía debe dinero.
      parcial: { label: 'Cobro parcial', class: 'bg-sky-100 text-sky-800' }
    };
    return config[status] || config.draft;
  };

  /** Duplica una factura o plantilla. La copia nace borrador.
   *
   * Antes esto armaba el cuerpo a mano y lo mandaba a `POST /invoices`, que no
   * existía: 404 disfrazado de "error al duplicar". Y cuando pasó a existir era
   * el alta directa, que EMITE con número — duplicar un borrador habría sacado
   * una factura de verdad.
   *
   * Qué se copia y qué se limpia lo decide el servidor, que es quien sabe qué
   * ata una factura a su emisión: el número, la remesa, el cobro, VeriFactu.
   */
  const handleDuplicateInvoice = async (invoice) => {
    if (duplicando) return;
    setDuplicando(invoice.id);
    try {
      const { data } = await axiosInstance.post(`/invoices/${invoice.id}/copy`);
      setInvoices((prev) => [data, ...prev]);
      toast.success('Duplicada como borrador', {
        description: 'Ábrela para ponerle sus fechas y su importe.'
      });
      fetchInvoices();
    } catch (error) {
      toast.error(mensajeDeError(error, 'No se pudo duplicar'));
    } finally {
      setDuplicando(null);
    }
  };

  // Helper: Check if recurring invoice has incomplete configuration
  const isRecurringIncomplete = (invoice) => {
    if (!invoice.is_recurring) return false;
    
    // Missing start_date or frequency = incomplete
    if (!invoice.start_date || !invoice.recurrence_frequency) return true;
    
    // A recurring invoice is incomplete if missing required fields
    return !invoice.start_date || !invoice.recurrence_frequency || !invoice.recurrence_interval;
  };

  // Helper: Format recurrence cadence
  const getRecurrenceCadence = (invoice) => {
    if (!invoice.recurrence_frequency || !invoice.recurrence_interval) return 'No configurada';
    
    const frequency = invoice.recurrence_frequency;
    const interval = invoice.recurrence_interval;
    
    if (frequency === 'mensual') {
      return `Mensual cada ${interval}`;
    } else if (frequency === 'semanal') {
      return `Semanal cada ${interval}`;
    }
    return 'No configurada';
  };

  // Helper: Calculate next creation date
  // La fecha de la proxima emision es `next_creation_date` de la plantilla, y
  // solo esa: es el campo por el que el cron busca las plantillas del dia
  // ({"next_creation_date": {"$lte": hoy}}) y el que el propio cron adelanta un
  // mes despues de emitir.
  //
  // Esta columna NO leia ese campo: recalculaba una fecha en el navegador
  // partiendo de `start_date`. Como el calculo empezaba con
  // "si start_date es futura, esa es la proxima" y las fechas se guardan en
  // UTC, el 1-sep una plantilla que arrancaba el 1-sep salia como
  // "Proxima creacion: 01/09/2026" -- 02:00 en hora española es mayor que la
  // medianoche local -- cuando el cron ya la habia adelantado al 01/10.
  //
  // O sea: la pantalla enseñaba una fecha inventada aqui mientras el cron
  // trabajaba con otra. No hay forma de saber si una repetitiva va a salir
  // mirando un numero que no es el que se consulta.
  const getNextCreationDate = (invoice) => {
    if (!invoice.next_creation_date) return null;
    return new Date(invoice.next_creation_date);
  };

  // Helper: Format date for display
  const formatDate = (date) => {
    if (!date) return '-';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Una fecha lista para un <input type="date">, en hora LOCAL.
  //
  // Antes se hacia con `toISOString().split('T')[0]`, que pasa la fecha a UTC.
  // En España eso resta dos horas, asi que una fecha guardada como
  // "2026-09-02T00:00:00" -- sin zona, que es justo como la guardaba el backend
  // al leerla de este mismo input -- se convertia en 01/09.
  //
  // Y el listado la pintaba con toLocaleDateString, que SI usa hora local. De
  // ahi que la lista dijera 02/09 y el formulario 01/09 del mismo dato. Las
  // plantillas importadas de Paygold no lo sufrian porque se guardan con zona
  // horaria explicita y entonces los dos caminos coinciden.
  //
  // Aqui se usan los componentes locales de la fecha, que es lo mismo que hace
  // toLocaleDateString: la lista y el formulario no pueden discrepar.
  // Abre el alta al llegar desde la ficha del cliente. Solo una vez: si se
  // reabriera en cada render, cerrar el diálogo sería imposible.
  const yaAbierto = useRef(false);
  useEffect(() => {
    if (clienteAFacturar && !yaAbierto.current) {
      yaAbierto.current = true;
      abrirNuevaFactura(clienteAFacturar);
    }
  }, [clienteAFacturar]);

  const paraInputFecha = (valor) => {
    if (!valor) return '';
    const d = valor instanceof Date ? valor : new Date(valor);
    if (Number.isNaN(d.getTime())) return '';
    const mes = String(d.getMonth() + 1).padStart(2, '0');
    const dia = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mes}-${dia}`;
  };

  // Helper: sustituye {mes}/{año} por el mes y año actuales (en español)
  // Solo para mostrar en el listado — la plantilla guarda el literal con variables.
  const MESES_ES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  const resolvePlaceholders = (text) => {
    if (!text) return text;
    const now = new Date();
    const mes = MESES_ES[now.getMonth()];
    const año = String(now.getFullYear());
    const mesNum = String(now.getMonth() + 1).padStart(2, '0');
    return text
      .replace(/\{mes\}/g, mes).replace(/\{MES\}/g, mes)
      .replace(/\{año\}/g, año).replace(/\{AÑO\}/g, año)
      .replace(/\{mes_num\}/g, mesNum);
  };

  const draftInvoices = invoices.filter(inv => inv.status === 'draft');

  // Una plantilla recurrente no es una factura emitida aunque su estado sea
  // 'accepted': nunca se expidio, solo espera a que el cron genere facturas.
  const editandoFacturaEmitida = Boolean(
    editingInvoice && editingInvoice.status !== 'draft' && !editingInvoice.is_recurring
  );

  // Un borrador suelto es una factura por emitir, igual que un alta: es lo que
  // deja Duplicar. Al cambiarle el cliente tiene que pasar lo mismo que al
  // elegirlo en un alta (Myriam, oct-2026: "copiar una factura cambiando el
  // cliente"). Antes se quedaba la forma de pago del cliente de la original y
  // no avisaba de que al nuevo no se le podia facturar.
  const editandoBorrador = Boolean(
    editingInvoice && editingInvoice.status === 'draft' && !editingInvoice.is_recurring
  );
  const comoUnAlta = creandoFactura || editandoBorrador;

  // Por qué no se le puede emitir. null = se puede, o todavía no se sabe.
  const motivoNoEmitir = comoUnAlta ? motivoParaNoEmitir(estadoFacturacion) : null;

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* Header */}
        {/* Se ajusta: en una pantalla estrecha los botones bajan debajo del
            título en vez de aplastarlo. */}
        <div className="flex flex-wrap items-center justify-between gap-y-3">
          <div>
            <h1 className="text-3xl font-bold text-[#212B35]">Facturas</h1>
            <p className="text-gray-600">Gestión de facturas y cobros</p>
          </div>
          {/* `flex-wrap`: en una pantalla estrecha la fila se ajusta en vez
              de salirse por el borde. Un boton que se sale no es un
              problema de estilo, es una accion a la que no se llega. */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {/* La puerta que faltaba: de las 859 facturas, 12 nacieron de un
                presupuesto. Cobrar algo suelto a un cliente no se podía. */}
            <Button
              onClick={abrirNuevaFactura}
              className="bg-[#9C0F17] hover:bg-[#7a0c12] text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nueva factura
            </Button>
            {/* Devolver una factura de la aplicación anterior. En contorno y no
                en relleno a propósito: emite un documento fiscal en negativo,
                pero no es la acción que se hace a diario. El botón que sí
                emite facturas se queda como el único en rojo macizo. */}
            <Button
              onClick={() => setDevolviendoAMano(true)}
              variant="outline"
              className="border-[#9C0F17] text-[#9C0F17] hover:bg-[#9C0F17] hover:text-white"
              data-testid="nueva-devolucion"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Nueva devolución
            </Button>
            {/* Cuatro acciones que se usan de uvas a peras, en un solo
                menu. Sueltas eran cuatro de los ocho botones de la barra, y
                con ocho no hay nada que se mire primero. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                  data-testid="menu-importar"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {recurringImporting ? 'Analizando...' : 'Importar'}
                  <ChevronDown className="w-4 h-4 ml-2 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>Traer facturas de fuera</DropdownMenuLabel>
                <DropdownMenuItem onSelect={() => setPdfDialogOpen(true)}>
                  <FileText className="w-4 h-4 mr-2" />
                  Desde un PDF
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setExcelConfigDialogOpen(true)}>
                  <Upload className="w-4 h-4 mr-2" />
                  Desde un Excel
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={handleRecurringPickFile}
                  disabled={recurringImporting}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Plantillas repetitivas
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {/* No es importar, pero tampoco merecia un boton en la barra:
                    el propio codigo la tenia marcada como "solo pruebas". */}
                <DropdownMenuLabel>Mantenimiento</DropdownMenuLabel>
                <DropdownMenuItem onSelect={handleRunRecurringCron}>
                  <Play className="w-4 h-4 mr-2" />
                  Generar repetitivas ahora
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <input
              ref={recurringFileRef}
              type="file"
              accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleRecurringFileSelected}
              style={{ display: 'none' }}
            />
            {/* Trabajar con la lista que hay debajo. */}
            <span className="mx-1 h-6 w-px bg-slate-200" aria-hidden="true" />
            <Button
              onClick={handleSelectAll}
              variant="outline"
              className="border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              {selectedInvoices.length === invoices.length && invoices.length > 0
                ? 'Deseleccionar todas'
                : 'Seleccionar todas'}
            </Button>
            {/* Descargar en ZIP: SIEMPRE visible, porque el camino normal
                es "todas las del filtro" y no marcar 350 casillas a mano
                (Angel, 30-sep-2026). Si hay marcadas, se lleva solo esas. */}
            <Button
              onClick={handleDescargarZip}
              disabled={descargando}
              variant="outline"
              className="border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900"
              title="Descarga las facturas en PDF, con un listado en Excel, para la gestoría"
              data-testid="descargar-zip"
            >
              <Download className="w-4 h-4 mr-2" />
              {descargando ? 'Preparando…'
                : textoDelBoton({ marcadas: selectedInvoices, total: totalInvoices })}
            </Button>
            {selectedInvoices.length > 0 && (
              <>
                <Button
                  onClick={handleApproveSelected}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Aprobar Seleccionadas ({selectedInvoices.length})
                </Button>
                <Button
                  onClick={handleDeleteSelected}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar Seleccionadas ({selectedInvoices.length})
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              {/* Periodo de emision.
                  El servidor ya entendia `desde`/`hasta` desde el 8-sep (se
                  puso para las tandas de remesas SEPA), pero esta pantalla no
                  lo ofrecia: no habia forma de acotar el mes que hay que
                  mandarle a la gestoria (Angel, 30-sep-2026).

                  Dos lineas con un trabajo cada una -- atajos arriba, rango
                  exacto abajo -- y el periodo escrito en cristiano al pie.
                  Antes eran cinco controles en una fila que se partia sola. */}
              <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
                <p className="text-sm font-semibold text-slate-800">
                  Periodo de emisión
                </p>

                <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
                  <label className="text-xs font-medium text-slate-600" htmlFor="filtro-mes">
                    Mes
                  </label>
                  {/* El camino normal: Angel saca las facturas mes a mes.
                      Resuelve las dos fechas de un clic, sin teclear ni
                      pelearse con que julio tiene 31 dias y septiembre 30. */}
                  <Input
                    id="filtro-mes" type="month" className="h-9 w-[11rem] bg-white"
                    value={mesDelRango({ desde, hasta })}
                    onChange={(e) => ponerMes(e.target.value)}
                    data-testid="filtro-mes"
                  />

                  <span className="h-6 w-px bg-slate-200" aria-hidden="true" />

                  <span className="text-xs font-medium text-slate-600">Trimestre</span>
                  {/* El periodo que pide la gestoria. Toma el anyo de la fecha
                      que haya puesta, o el de hoy. */}
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4].map((t) => (
                      <Button
                        key={t} type="button" variant="outline" size="sm"
                        onClick={() => ponerTrimestre(t)}
                        className="h-9 w-10 border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                        title={`Todo el trimestre T${t}`}
                        data-testid={`filtro-t${t}`}
                      >
                        T{t}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
                  <label className="text-xs font-medium text-slate-600" htmlFor="filtro-desde">
                    Desde
                  </label>
                  <Input
                    id="filtro-desde" type="date" className="h-9 w-[10.5rem] bg-white"
                    value={desde}
                    onChange={(e) => { setDesde(e.target.value); setPage(1); }}
                    data-testid="filtro-desde"
                  />
                  <label className="text-xs font-medium text-slate-600" htmlFor="filtro-hasta">
                    Hasta
                  </label>
                  <Input
                    id="filtro-hasta" type="date" className="h-9 w-[10.5rem] bg-white"
                    value={hasta}
                    onChange={(e) => { setHasta(e.target.value); setPage(1); }}
                    data-testid="filtro-hasta"
                  />
                  {(desde || hasta) && (
                    <Button
                      type="button" variant="ghost" size="sm"
                      onClick={() => { setDesde(''); setHasta(''); setPage(1); }}
                      className="h-9 text-slate-600 hover:text-slate-900"
                      data-testid="filtro-fechas-limpiar"
                    >
                      <X className="w-3.5 h-3.5 mr-1" /> Quitar fechas
                    </Button>
                  )}
                </div>

                {/* Lo que de verdad faltaba. El boton de descargar dice "las
                    1.431 del filtro" y ese numero, solo, no dice DE QUE. Sin
                    esta linea, bajarse el historico entero creyendo que iba un
                    mes es un clic (Angel, 1-oct-2026). */}
                <p className="mt-3 text-sm text-slate-700">
                  <span className="font-semibold">
                    {descripcionDelPeriodo({ desde, hasta })}
                  </span>
                  {totalInvoices > 0 && (
                    <span className="text-slate-500">
                      {' · '}{totalInvoices.toLocaleString('es-ES')}
                      {totalInvoices === 1 ? ' factura' : ' facturas'}
                    </span>
                  )}
                  {!desde && !hasta && (
                    <span className="ml-2 text-xs text-amber-700">
                      Elige un mes antes de descargar para la gestoría.
                    </span>
                  )}
                </p>
              </div>

              {/* Buscador por cliente */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Buscar</p>
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Cliente, NIF, email, nº de factura o referencia..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 pr-9"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => setSearchTerm('')}
                      className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                      aria-label="Limpiar búsqueda"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {debouncedSearch && !loadingInvoices && (
                  <p className="mt-2 text-xs text-gray-600">
                    {totalInvoices} {totalInvoices === 1 ? 'factura' : 'facturas'} para "{debouncedSearch}"
                  </p>
                )}
              </div>

              {/* Recurring Filter */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Tipo de factura</p>
                <div className="flex gap-2 items-center">
                  <Button
                    variant={filterRecurring === 'all' ? 'default' : 'outline'}
                    onClick={() => setFilterRecurring('all')}
                    size="sm"
                    className={filterRecurring === 'all' ? 'bg-[#9C0F17] hover:bg-[#7a0c12]' : ''}
                  >
                    Todas
                  </Button>
                  <Button
                    variant={filterRecurring === 'recurring' ? 'default' : 'outline'}
                    onClick={() => setFilterRecurring('recurring')}
                    size="sm"
                    className={filterRecurring === 'recurring' ? 'bg-[#9C0F17] hover:bg-[#7a0c12]' : ''}
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Repetitivas
                  </Button>
                  <Button
                    variant={filterRecurring === 'unique' ? 'default' : 'outline'}
                    onClick={() => setFilterRecurring('unique')}
                    size="sm"
                    className={filterRecurring === 'unique' ? 'bg-[#9C0F17] hover:bg-[#7a0c12]' : ''}
                  >
                    Únicas
                  </Button>
                </div>
              </div>

              {/* Status Filter */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Estado</p>
                <div className="flex gap-2 items-center flex-wrap">
                  <Button
                    variant={filterStatus === 'all' ? 'default' : 'outline'}
                    onClick={() => setFilterStatus('all')}
                    size="sm"
                    className={filterStatus === 'all' ? 'bg-[#9C0F17] hover:bg-[#7a0c12]' : ''}
                  >
                    Todos
                  </Button>
                  <Button
                    variant={filterStatus === 'draft' ? 'default' : 'outline'}
                    onClick={() => setFilterStatus('draft')}
                    size="sm"
                    className={filterStatus === 'draft' ? 'bg-[#9C0F17] hover:bg-[#7a0c12]' : ''}
                  >
                    Borrador
                  </Button>
                  <Button
                    variant={filterStatus === 'accepted' ? 'default' : 'outline'}
                    onClick={() => setFilterStatus('accepted')}
                    size="sm"
                    className={filterStatus === 'accepted' ? 'bg-[#9C0F17] hover:bg-[#7a0c12]' : ''}
                  >
                    Aceptadas
                  </Button>
                  <Button
                    variant={filterStatus === 'paid' ? 'default' : 'outline'}
                    onClick={() => setFilterStatus('paid')}
                    size="sm"
                    className={filterStatus === 'paid' ? 'bg-[#9C0F17] hover:bg-[#7a0c12]' : ''}
                  >
                    Pagadas
                  </Button>
                  <Button
                    variant={filterStatus === 'parcial' ? 'default' : 'outline'}
                    onClick={() => setFilterStatus('parcial')}
                    size="sm"
                    className={filterStatus === 'parcial' ? 'bg-[#9C0F17] hover:bg-[#7a0c12]' : ''}
                    data-testid="filtro-parcial"
                  >
                    Cobro parcial
                  </Button>
                  {/* Lo que se debe, venza cuando venza. Las vencidas son
                      una parte de estas. */}
                  <Button
                    variant={filterStatus === 'pendientes' ? 'default' : 'outline'}
                    onClick={() => setFilterStatus('pendientes')}
                    size="sm"
                    className={filterStatus === 'pendientes' ? 'bg-[#9C0F17] hover:bg-[#7a0c12]' : ''}
                    data-testid="filtro-pendientes"
                  >
                    Pendientes
                  </Button>
                  <Button
                    variant={filterStatus === 'overdue' ? 'default' : 'outline'}
                    onClick={() => setFilterStatus('overdue')}
                    size="sm"
                    className={filterStatus === 'overdue' ? 'bg-[#9C0F17] hover:bg-[#7a0c12]' : ''}
                  >
                    Vencidas
                  </Button>
                  <Button
                    variant={filterStatus === 'returned' ? 'default' : 'outline'}
                    onClick={() => setFilterStatus('returned')}
                    size="sm"
                    className={filterStatus === 'returned' ? 'bg-[#9C0F17] hover:bg-[#7a0c12]' : ''}
                  >
                    Devueltas
                  </Button>
                  {/* Impagos del banco. Aparte de "Devueltas", que son las
                      rectificativas: son dos cosas distintas y mezclarlas
                      dejaría el cobro pendiente escondido entre los abonos. */}
                  <Button
                    variant={filterStatus === 'recibo_devuelto' ? 'default' : 'outline'}
                    onClick={() => setFilterStatus('recibo_devuelto')}
                    size="sm"
                    className={filterStatus === 'recibo_devuelto' ? 'bg-[#9C0F17] hover:bg-[#7a0c12]' : ''}
                    data-testid="filtro-recibo-devuelto"
                  >
                    Recibos devueltos
                  </Button>
                </div>
              </div>

              {/* Si le ha llegado al cliente por correo. Es un filtro aparte
                  del estado porque se cruza con todos: una pagada puede estar
                  sin enviar, y un borrador no. */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Correo</p>
                <div className="flex gap-2 items-center flex-wrap">
                  {[['all', 'Todas'], ['sent', 'Enviadas'], ['unsent', 'Sin enviar']].map(([valor, texto]) => (
                    <Button
                      key={valor}
                      variant={filterEmail === valor ? 'default' : 'outline'}
                      onClick={() => setFilterEmail(valor)}
                      size="sm"
                      className={filterEmail === valor ? 'bg-[#9C0F17] hover:bg-[#7a0c12]' : ''}
                      data-testid={`filtro-correo-${valor}`}
                    >
                      {valor === 'sent' && <Mail className="w-3.5 h-3.5 mr-1" />}
                      {texto}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Invoices List */}
        <div className="grid gap-4">
          {invoices.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                <Receipt className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No hay facturas disponibles</p>
              </CardContent>
            </Card>
          ) : filterRecurring === 'recurring' ? (
            // SPECIAL VIEW FOR RECURRING INVOICES
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#9C0F17] text-white">
                      <tr>
                        <th className="p-3 text-left">Cliente</th>
                        <th className="p-3 text-right">Total</th>
                        <th className="p-3 text-center">Fecha Inicio</th>
                        <th className="p-3 text-center">Próxima Creación</th>
                        <th className="p-3 text-center">Fecha Fin</th>
                        <th className="p-3 text-center">Cadencia</th>
                        <th className="p-3 text-center">Auto-crear</th>
                        <th className="p-3 text-center">Estado</th>
                        <th className="p-3 text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((invoice) => {
                        const isIncomplete = isRecurringIncomplete(invoice);
                        const nextDate = getNextCreationDate(invoice);
                        const cadence = getRecurrenceCadence(invoice);
                        
                        return (
                          <tr 
                            key={invoice.id} 
                            className={`border-b hover:bg-gray-50 ${isIncomplete ? 'bg-red-50' : ''}`}
                          >
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                {isIncomplete && (
                                  <span className="text-red-600 font-bold" title="Configuración incompleta">⚠️</span>
                                )}
                                <span className="font-medium">{invoice.customer_name}</span>
                              </div>
                              {invoice.invoice_reference && (
                                <div className="text-xs text-gray-600 mt-1 leading-tight" title={invoice.invoice_reference}>
                                  📄 {resolvePlaceholders(invoice.invoice_reference)}
                                </div>
                              )}
                              {invoice.platform_email && (
                                <div className="text-xs text-gray-500 mt-0.5 leading-tight" title="Email de la plataforma C10">
                                  ✉️ {invoice.platform_email}
                                </div>
                              )}
                            </td>
                            <td className="p-3 text-right font-semibold">{invoice.total?.toFixed(2)}€</td>
                            <td className="p-3 text-center text-sm">
                              {invoice.start_date ? formatDate(invoice.start_date) : (
                                <span className="text-red-600 font-semibold">Sin definir</span>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              {nextDate ? (
                                <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm font-medium">
                                  {formatDate(nextDate)}
                                </span>
                              ) : isIncomplete ? (
                                <span className="text-red-600 font-semibold text-sm">Configurar</span>
                              ) : (
                                // Sin `next_creation_date` el cron no la
                                // encuentra: su consulta filtra justo por ese
                                // campo. Decir "Finalizada" daba a entender que
                                // habia llegado a su fin, y lo mismo nunca
                                // llego a arrancar.
                                <span
                                  className="text-gray-500 text-sm"
                                  title="Sin fecha de próxima emisión guardada: el cron no la va a coger. Edítala y guarda para fijarla."
                                >
                                  No se emitirá
                                </span>
                              )}
                            </td>
                            <td className="p-3 text-center text-sm">
                              {invoice.end_date ? formatDate(invoice.end_date) : (
                                <span className="text-blue-600">Sin límite</span>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              <span className={`text-sm font-medium ${isIncomplete ? 'text-red-600' : ''}`}>
                                {cadence}
                              </span>
                            </td>
                            <td className="p-3 text-center">
                              {invoice.status === 'draft' || isIncomplete ? (
                                <Badge className="bg-yellow-100 text-yellow-800" title="Factura en borrador o incompleta">
                                  🔒 Borrador
                                </Badge>
                              ) : (
                                <Badge className="bg-green-100 text-green-800">✅ Definitiva</Badge>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              {isIncomplete ? (
                                <Badge className="bg-red-100 text-red-800">⚠️ Incompleta</Badge>
                              ) : (
                                <Badge className="bg-green-100 text-green-800">✅ Configurada</Badge>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              <div className="flex gap-2 justify-center">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleOpenEdit(invoice)}
                                  className="text-blue-600 hover:text-blue-800"
                                >
                                  <Edit className="w-4 h-4 mr-1" />
                                  Editar
                                </Button>
                                {invoice.status === 'draft' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDuplicateInvoice(invoice)}
                                    disabled={duplicando === invoice.id}
                                    className="text-slate-600 hover:text-slate-900"
                                    title="Duplicar como borrador"
                                  >
                                    <Copy className="w-4 h-4 mr-1" />
                                    {duplicando === invoice.id ? 'Duplicando...' : 'Duplicar'}
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteInvoice(invoice.id)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  title="Eliminar factura repetitiva"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ) : (
            // LISTADO DE FACTURAS (unicas y "todas")
            //
            // Antes era una tarjeta por factura, cada una con seis botones de
            // texto. Con 248 facturas emitidas el mismo dia eso obliga a
            // desplazarse muchisimo y, sobre todo, no deja comparar: para saber
            // cual es cual habia que abrirlas. En tabla, el numero, el cliente y
            // el importe caen en columna y se leen de un vistazo.
            //
            // El orden lo pone el backend: numero descendente, la mas reciente
            // arriba. Ver GET /invoices.
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#9C0F17] text-white">
                      <tr>
                        <th className="p-3 w-10"></th>
                        <th className="p-3 text-left">Nº Factura</th>
                        <th className="p-3 text-left">Cliente</th>
                        <th className="p-3 text-center">Emisión</th>
                        <th className="p-3 text-center">Vencimiento</th>
                        <th className="p-3 text-right">Total</th>
                        <th className="p-3 text-center">Estado</th>
                        <th className="p-3 text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((invoice) => {
                        const statusBadge = getStatusBadge(invoice.status);
                        const isDraft = invoice.status === 'draft';
                        const isSelected = selectedInvoices.includes(invoice.id);
                        const esPlantilla = invoice.is_recurring;

                        return (
                          <tr
                            key={invoice.id}
                            className={`border-b hover:bg-gray-50 ${isSelected ? 'bg-green-50' : ''}`}
                          >
                            <td className="p-3">
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => handleToggleSelection(invoice.id)}
                              />
                            </td>

                            <td className="p-3">
                              <div className="font-mono font-bold whitespace-nowrap">
                                {invoice.invoice_number || <span className="text-gray-400 font-sans font-normal">Sin número</span>}
                              </div>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {esPlantilla && (
                                  <Badge className="bg-purple-100 text-purple-800 text-xs">
                                    <RefreshCw className="w-3 h-3 mr-1" />
                                    Repetitiva
                                  </Badge>
                                )}
                                {invoice.is_return && (
                                  <Badge className="bg-orange-100 text-orange-800 text-xs">Devolución</Badge>
                                )}
                                {invoice.is_proforma && (
                                  <Badge className="bg-yellow-100 text-yellow-800 text-xs">Proforma</Badge>
                                )}
                              </div>
                            </td>

                            <td className="p-3">
                              <div className="font-medium">
                                {invoice.customer_fiscal_name || invoice.customer_name || 'Sin nombre'}
                              </div>
                              {/* Ha devuelto alguna vez, aunque ahora conste
                                  pagada. Es lo que hay que ver antes de
                                  renovarle a alguien. */}
                              {invoice.recibo_devuelto && invoice.status !== 'recibo_devuelto' && (
                                <div className="text-xs text-amber-700 leading-tight"
                                     title={`El recibo se devolvió ${invoice.recibos_devueltos || 1} vez/veces`}>
                                  ⚠ Tuvo recibo devuelto
                                  {invoice.recibos_devueltos > 1 ? ` (${invoice.recibos_devueltos}×)` : ''}
                                </div>
                              )}
                              {invoice.invoice_reference && (
                                <div className="text-xs text-gray-600 leading-tight" title={invoice.invoice_reference}>
                                  📄 {invoice.invoice_reference}
                                </div>
                              )}
                            </td>

                            <td className="p-3 text-center text-sm whitespace-nowrap">
                              {formatDate(invoice.issue_date)}
                            </td>

                            {/* Una plantilla no vence, y las 247 importadas
                                tienen due_date a null: pintarlo sin mirar daba
                                "Invalid Date". */}
                            <td className="p-3 text-center text-sm whitespace-nowrap">
                              {esPlantilla ? (
                                <span className="text-gray-400">No vence</span>
                              ) : invoice.due_date ? (
                                formatDate(invoice.due_date)
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>

                            <td className="p-3 text-right font-bold whitespace-nowrap">
                              {(invoice.total ?? 0).toFixed(2)}€
                              {/* Lo que falta, debajo del total. Sin esto, una
                                  factura a medias parece cobrada o parece sin
                                  cobrar, según quién la mire. */}
                              {invoice.status === 'parcial' && (() => {
                                const c = resumenDeCobro(invoice);
                                return (
                                  <span className="block text-xs font-normal text-sky-700"
                                        title={`Cobrado ${c.cobrado.toFixed(2)} € de ${c.total.toFixed(2)} €`}>
                                    faltan {c.pendiente.toFixed(2)}€
                                  </span>
                                );
                              })()}
                            </td>

                            <td className="p-3 text-center">
                              <div className="flex items-center justify-center gap-1.5">
                                <Badge className={statusBadge.class}>{statusBadge.label}</Badge>
                                {/* Si le ha llegado por correo. Una plantilla
                                    no se manda nunca, asi que no se le pinta
                                    nada: un "sin enviar" ahi seria un falso
                                    pendiente en cada fila. */}
                                {!esPlantilla && (() => {
                                  const envio = estadoDeEnvio(invoice);
                                  return envio.enviada ? (
                                    <MailCheck
                                      className="w-4 h-4 text-green-600 shrink-0"
                                      title={envio.titulo}
                                      aria-label={envio.titulo}
                                      data-testid="correo-enviado"
                                    />
                                  ) : (
                                    <Mail
                                      className={`w-4 h-4 shrink-0 ${invoice.email_ultimo_error ? 'text-red-500' : 'text-slate-300'}`}
                                      title={envio.titulo}
                                      aria-label={envio.titulo}
                                      data-testid="correo-sin-enviar"
                                    />
                                  );
                                })()}
                              </div>
                            </td>

                            {/* Iconos en vez de botones con texto: seis botones
                                de texto por fila no caben, y el objetivo de esta
                                pantalla es comparar facturas, no pulsarlas. */}
                            <td className="p-3">
                              <div className="flex gap-1 justify-center">
                                {invoice.invoice_number && (
                                  <>
                                    <Button
                                      variant="ghost" size="sm" title="Descargar PDF"
                                      onClick={() => handleDownloadPDF(invoice.id, invoice.invoice_number)}
                                      className="text-red-600 hover:text-red-800 h-8 w-8 p-0"
                                    >
                                      <Download className="w-4 h-4" />
                                    </Button>
                                    {/* Link de pago. Solo en las que se cobran
                                        con tarjeta y no están cobradas: un link
                                        vivo sobre algo ya pagado es la única
                                        forma de cobrar dos veces.

                                        En gris a propósito: la fila ya tiene
                                        cuatro colores y cada uno significa algo
                                        (rojo PDF, azul email, verde aprobar,
                                        naranja rectificar). Un quinto tono no
                                        añade información, solo ruido. */}
                                    {invoice.payment_method === 'link'
                                      && invoice.status !== 'paid' && (
                                      <Button
                                        variant="ghost" size="sm" title="Generar link de pago"
                                        onClick={() => generarLinkDePago(invoice)}
                                        disabled={generandoLink === invoice.id}
                                        className="text-slate-600 hover:text-slate-900 h-8 w-8 p-0"
                                      >
                                        <Link2 className="w-4 h-4" />
                                      </Button>
                                    )}
                                    <Button
                                      variant="ghost" size="sm" title="Enviar por email"
                                      onClick={() => setEnviando(invoice)}
                                      className="text-blue-600 hover:text-blue-800 h-8 w-8 p-0"
                                    >
                                      <Mail className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}

                                <Button
                                  variant="ghost" size="sm"
                                  title={isDraft || esPlantilla ? 'Editar' : 'Corregir factura emitida (queda registrado)'}
                                  onClick={() => handleOpenEdit(invoice)}
                                  className="text-gray-700 hover:text-gray-900 h-8 w-8 p-0"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>

                                {isDraft && (
                                  <Button
                                    variant="ghost" size="sm" title="Aprobar"
                                    onClick={() => handleUpdateStatus(invoice.id, 'accepted')}
                                    className="text-green-600 hover:text-green-800 h-8 w-8 p-0"
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </Button>
                                )}

                                {/* "Marcar pagada", no "Pagada".
                                    En la misma fila, la columna Estado ya dice
                                    "Aceptada" y este boton decia "Pagada" en
                                    verde: se leia como que la factura estaba
                                    cobrada, cuando es el boton para cobrarla.
                                    Y en verde solo la insignia de Estado, que
                                    es la que informa; un boton verde al lado
                                    compite con ella. */}
                                {/* También en las de recibo devuelto: cuando
                                    el cliente paga, vuelve a Pagada y la marca
                                    de que se devolvió se queda. */}
                                {/* También en las de cobro parcial: es donde se
                                    apunta el plazo siguiente. */}
                                {/* Ni en las plantillas, que no las paga nadie
                                    (lo decía Myriam), ni en las de 0, que no
                                    tienen cobro que apuntar. */}
                                {!esPlantilla && !sinImporte(invoice)
                                  && (invoice.status === 'accepted'
                                  || invoice.status === 'recibo_devuelto'
                                  || invoice.status === 'parcial') && (
                                  <Button
                                    variant="ghost" size="sm"
                                    title="Apuntar un cobro de esta factura"
                                    onClick={() => handleMarcarPagada(invoice)}
                                    className="text-slate-700 hover:text-slate-900 h-8 px-2 text-xs whitespace-nowrap"
                                  >
                                    {invoice.status === 'parcial' ? 'Apuntar cobro' : 'Marcar pagada'}
                                  </Button>
                                )}
                                {!esPlantilla && sinImporte(invoice) && invoice.status === 'accepted' && (
                                  <Button
                                    variant="ghost" size="sm"
                                    title="No hay nada que cobrar: pasa a pagada y deja de salir como pendiente"
                                    onClick={() => handleUpdateStatus(invoice.id, 'paid')}
                                    className="text-slate-700 hover:text-slate-900 h-8 px-2 text-xs whitespace-nowrap"
                                    data-testid="saldar-a-cero"
                                  >
                                    Marcar saldada
                                  </Button>
                                )}

                                {/* El banco devolvió el recibo. Solo en las que
                                    constan cobradas: una que nadie ha pagado
                                    todavía no tiene recibo que devolver. */}
                                {invoice.status === 'paid' && !esPlantilla && (
                                  <Button
                                    variant="ghost" size="sm"
                                    title="El banco ha devuelto el recibo de esta factura"
                                    onClick={() => {
                                      setDevolviendoRecibo(invoice);
                                      setFechaDevolucion(paraInputFecha(new Date()));
                                      setMotivoDevolucion('');
                                    }}
                                    className="text-amber-700 hover:text-amber-900 h-8 px-2 text-xs whitespace-nowrap"
                                    data-testid="marcar-recibo-devuelto"
                                  >
                                    Recibo devuelto
                                  </Button>
                                )}

                                {invoice.is_proforma && !invoice.proforma_invoice_id && (
                                  <Button
                                    variant="ghost" size="sm" title="Convertir a factura definitiva"
                                    onClick={() => handleConvertProformaToDefinitive(invoice.id, invoice.invoice_number)}
                                    className="text-purple-600 hover:text-purple-800 h-8 px-2 text-xs"
                                  >
                                    Definitiva
                                  </Button>
                                )}

                                {/* Una abonada entera no se vuelve a abonar: cada clic
                                    emitía otro AB26 (Carlos, 3-nov-2026). El servidor
                                    también lo rechaza; esto es para no ofrecerlo. */}
                                {(invoice.status === 'accepted' || invoice.status === 'paid') && !invoice.is_return
                                  && !invoice.abonada_entera && (
                                  <Button
                                    variant="ghost" size="sm" title="Emitir factura rectificativa (abono)"
                                    onClick={() => setRectificando(invoice)}
                                    className="text-orange-600 hover:text-orange-800 h-8 w-8 p-0"
                                  >
                                    <RotateCcw className="w-4 h-4" />
                                  </Button>
                                )}

                                <Button
                                  variant="ghost" size="sm" title="Eliminar"
                                  onClick={() => handleDeleteInvoice(invoice.id, invoice.invoice_number, invoice.customer_name)}
                                  className="text-red-600 hover:text-red-800 h-8 w-8 p-0"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Paginación */}
        {totalInvoices > 0 && totalPages > 1 && (
          <div className="flex items-center justify-between bg-white border rounded-lg px-4 py-3 text-sm">
            <div className="text-gray-600">
              Mostrando <strong>{(page - 1) * pageSize + 1}</strong>
              {' – '}
              <strong>{Math.min(page * pageSize, totalInvoices)}</strong>
              {' de '}
              <strong>{totalInvoices}</strong> facturas
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(1)}
                disabled={page === 1 || loadingInvoices}
              >
                «
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1 || loadingInvoices}
              >
                Anterior
              </Button>
              <span className="text-gray-700 font-medium px-2">
                Página {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loadingInvoices}
              >
                Siguiente
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages || loadingInvoices}
              >
                »
              </Button>
            </div>
          </div>
        )}

        {/* Edit Invoice Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {creandoFactura
                  ? 'Nueva factura'
                  : editandoFacturaEmitida
                    ? 'Corregir factura emitida'
                    : editingInvoice?.is_recurring
                      ? 'Editar plantilla recurrente'
                      : 'Editar Factura'}
              </DialogTitle>
              <DialogDescription>
                {creandoFactura && 'Se emitirá con número en cuanto le des a Emitir'}
                {/* Una plantilla no lleva numero: no es una factura, es la
                    descripcion de lo que hay que facturar cada mes. Cada
                    factura que sale de ella se numera al emitirse. Enseñar
                    aqui un numero la hacia parecer una factura ya hecha. */}
                {editingInvoice && (
                  editingInvoice.is_recurring
                    ? 'Sin número — se numera cada factura al emitirla'
                    : editandoFacturaEmitida
                      ? `${editingInvoice.invoice_number || 'Sin número'} — ya emitida`
                      : `${editingInvoice.invoice_number || 'Sin número'} — borrador`
                )}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Aviso al corregir una factura que ya salio. No es decorativo:
                  el usuario tiene que saber que esto no es lo mismo que tocar
                  un borrador, y que hay dos cosas que no va a poder cambiar. */}
              {editandoFacturaEmitida && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                  <p className="font-semibold">Esta factura ya está emitida</p>
                  <p className="mt-1">
                    Puedes corregir cliente, líneas, importes, forma de pago y referencia.
                    El <strong>número</strong> y la <strong>fecha de emisión</strong> no se
                    pueden cambiar desde aquí: para eso hay que emitir una rectificativa.
                  </p>
                  <p className="mt-2 text-amber-800">
                    El cambio queda registrado con tu usuario, y el asiento contable y el
                    libro de facturas emitidas se actualizan solos.
                  </p>
                </div>
              )}

              {/* Cliente. Buscador contra el servidor, no un desplegable:
                  hay 2.044 clientes activos y la lista traía 100. */}
              <div>
                <Label htmlFor="customer">Cliente *</Label>
                <ClientSearchSelect
                  label={null}
                  value={editForm.customer_id}
                  onValueChange={(id, ficha) => {
                    // En un alta viene puesta la forma de pago del cliente. Se
                    // puede cambiar: un pedido suelto a veces va por link o en
                    // efectivo aunque su cuota mensual se le gire.
                    //
                    // Al cambiar de cliente se reemplaza, no se conserva: la
                    // forma de pago es de ÉL, no de la factura que se estaba
                    // escribiendo. En un borrador igual: ver `comoUnAlta`.
                    setEditForm({
                      ...editForm,
                      customer_id: id,
                      ...(comoUnAlta ? { payment_method: ficha?.payment_method || '' } : {}),
                    });
                    setClienteElegido(ficha);
                    if (comoUnAlta) comprobarSiSePuedeFacturar(id);
                  }}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Busca por nombre, email, NIF, teléfono o nombre fiscal.
                </p>
              </div>

              {/* Lo que le falta para poder facturarle, dicho AL ELEGIRLO.
                  329 de 2.037 clientes no pasan el candado: enterarse después
                  de teclear seis líneas es tarde y se pierde el trabajo. */}
              {motivoNoEmitir && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                  <p className="font-semibold">Todavía no se le puede facturar</p>
                  <p className="mt-1">{motivoNoEmitir}</p>
                  <p className="mt-2 text-amber-800">
                    Se rellenan en <strong>Clientes → Editar</strong>. Es el mismo
                    requisito que al convertir un presupuesto: emitir sin estos
                    datos obliga a hacer una rectificativa después.
                  </p>
                </div>
              )}

              {/* Que se vea también cuando SÍ se puede: un aviso que solo
                  aparece cuando algo va mal deja la duda de si ha comprobado
                  algo o no. */}
              {comoUnAlta && estadoFacturacion && !motivoNoEmitir && (
                <p className="text-xs text-emerald-700 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Tiene todos los datos para facturar.
                </p>
              )}

              {/* Estado. En una factura ya emitida no se ofrece el selector:
                  solo tiene 'draft' y 'accepted', asi que abrir una factura
                  PAGADA aqui dejaba el desplegable vacio y cualquier eleccion
                  la habria devuelto a borrador. El estado de una emitida se
                  cambia con sus propios botones de la lista. */}
              {/* En un alta no hay estado que elegir: nace emitida. */}
              {creandoFactura ? null : editandoFacturaEmitida ? (
                <div>
                  <Label>Estado</Label>
                  <div className="mt-1 flex items-center gap-2">
                    <Badge className={getStatusBadge(editForm.status).class}>
                      {getStatusBadge(editForm.status).label}
                    </Badge>
                    <span className="text-xs text-gray-500">
                      El estado se cambia desde los botones de la lista de facturas
                    </span>
                  </div>
                </div>
              ) : (
                <div>
                  <Label htmlFor="status">Estado *</Label>
                  <Select
                    value={editForm.status}
                    onValueChange={(value) => setEditForm({ ...editForm, status: value })}
                  >
                    <SelectTrigger id="status">
                      <SelectValue placeholder="Selecciona estado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">📝 Borrador</SelectItem>
                      <SelectItem value="accepted">✅ Aceptada</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    {editForm.status === 'draft'
                      ? (editingInvoice?.is_recurring
                          ? 'En borrador la plantilla no emite. Acéptala para que el cron empiece a generar facturas'
                          : 'La factura está en borrador y puede editarse libremente')
                      : (editingInvoice?.is_recurring
                          // Una plantilla aceptada NO recibe numero: el numero
                          // se reserva al emitir cada factura. Este aviso decia
                          // lo contrario y era justo lo que estaba pasando.
                          ? 'Plantilla activa. No lleva número: se numera cada factura al emitirla'
                          : 'Al aceptar, se asignará número de factura automáticamente')}
                  </p>
                </div>
              )}

              {/* Vencimiento.
                  En una plantilla no se pide: no se usa para nada. El cron le
                  pone a cada factura que genera la fecha del dia en que la
                  emite, sin mirar la plantilla. El campo estaba ahi con un
                  asterisco de obligatorio -- que ademas no validaba nada --
                  pidiendo un dato que no iba a ningun sitio.
                  Y en una factura normal tampoco es obligatorio: no tener
                  vencimiento es lo corriente aqui. */}
              {editingInvoice?.is_recurring ? (
                <div>
                  <Label>Fecha de Vencimiento</Label>
                  <p className="text-sm text-gray-600 mt-1">
                    No aplica a una plantilla. Cada factura que se genere vencerá
                    el mismo día en que se emita.
                  </p>
                </div>
              ) : (
                <div>
                  <Label htmlFor="due_date">Fecha de Vencimiento</Label>
                  <Input
                    id="due_date"
                    type="date"
                    value={editForm.due_date}
                    onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Opcional. Si se deja vacía, la factura no muestra vencimiento.
                  </p>
                </div>
              )}

              {/* Items */}
              <div>
                <div className="mb-3">
                  <Label className="mb-2 block">Añadir conceptos</Label>
                  {/* Un buscador y no dos desplegables encadenados: el
                      catálogo es grande y elegir tipo y luego producto sin
                      poder escribir era poco usable (Angel, 15-sep-2026). */}
                  <ProductSearchSelect
                    products={products}
                    onPick={(producto) => handleAddProduct(producto.id)}
                  />
                </div>

                {/* De qué mes es el servicio. Solo en el alta: resuelve
                    {mes}/{año} de las líneas al emitir. Al editar una emitida
                    el texto ya está fijo y no hay nada que resolver. */}
                {creandoFactura && (
                  <div className="mb-3 flex flex-wrap items-end gap-3">
                    <div>
                      <Label htmlFor="periodo">Mes del servicio</Label>
                      <Input
                        id="periodo" type="month"
                        value={editForm.periodo}
                        onChange={(e) => setEditForm({ ...editForm, periodo: e.target.value })}
                        className="w-44"
                        data-testid="periodo-servicio"
                      />
                    </div>
                    <p className="text-xs text-gray-500 pb-2">
                      Es lo que ponen <code>{'{mes}'}</code> y <code>{'{año}'}</code> en
                      los conceptos. En septiembre se factura agosto: cámbialo aquí.
                    </p>
                  </div>
                )}

                {editForm.line_items.length === 0 ? (
                  <div className="text-center py-8 border rounded-lg bg-gray-50">
                    <p className="text-gray-500">No hay items. Selecciona productos del menú de arriba.</p>
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-[#9C0F17] text-white">
                        <tr>
                          <th className="p-2 text-left">PRODUCTO</th>
                          <th className="p-2 text-right">PRECIO</th>
                          <th className="p-2 text-center">CANT.</th>
                          <th className="p-2 text-center">DESC. %</th>
                          <th className="p-2 text-right">SUBTOTAL</th>
                          <th className="p-2 text-right">IMP.</th>
                          <th className="p-2 text-right">TOTAL</th>
                          <th className="p-2 text-center">-</th>
                        </tr>
                      </thead>
                      <tbody>
                        {editForm.line_items.map((item, index) => (
                          <tr key={index} className="border-t">
                            <td className="p-2">
                              {/* La descripción se edita en borradores repetitivos
                                  y en el alta a mano. En el alta hace falta para
                                  decir de qué mes es el servicio o a qué
                                  restaurante va (Angel, 15-sep-2026): el nombre
                                  del catálogo se queda corto. */}
                              {/* El texto se edita SIEMPRE, también al corregir
                                  una factura ya emitida (Angel, 22-sep-2026:
                                  F26-12233). Antes solo en el alta y en las
                                  plantillas, lo que era incoherente: en esa
                                  misma fila ya se podían cambiar la cantidad y
                                  el descuento, pero no el concepto.

                                  Textarea y no Input: la descripción trae
                                  saltos de línea y en un Input se aplastan. */}
                              <div className="space-y-1">
                                <Textarea
                                  value={item.description}
                                  onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                                  className="w-full min-h-0 text-sm"
                                  rows={Math.min(6, Math.max(1, String(item.description || '').split('\n').length))}
                                  placeholder="Concepto. Admite varias líneas, {mes} y {año}"
                                  data-testid={`linea-${index}-descripcion`}
                                />
                                <div className="flex items-center gap-1 flex-wrap">
                                  {/* Se pegan al final con un espacio: es donde
                                      van casi siempre ("Licencia TPV Agosto 2026"). */}
                                  {['{mes}', '{año}'].map((v) => (
                                    <button
                                      type="button" key={v}
                                      onClick={() => handleItemChange(
                                        index, 'description',
                                        `${(item.description || '').replace(/\s+$/, '')} ${v}`.trim())}
                                      className="text-[11px] px-1.5 py-0.5 rounded border border-slate-300 text-slate-600 hover:bg-slate-100 font-mono"
                                      title={`Añade ${v} al final del concepto`}
                                    >
                                      + {v}
                                    </button>
                                  ))}
                                  {llevaVariables(item.description) && (
                                    editForm.is_recurring ? (
                                      /* En una plantilla NO se resuelven aquí:
                                         las rellena el cron cada mes, y por eso
                                         no se puede enseñar un mes concreto. */
                                      <span className="text-[11px] text-slate-500 ml-1">
                                        Se rellena al generar cada factura
                                      </span>
                                    ) : (
                                      <span className="text-[11px] text-slate-500 ml-1 whitespace-pre-line">
                                        Saldrá: <em>{resolverVariables(item.description, editForm.periodo)}</em>
                                      </span>
                                    )
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="p-2 text-right whitespace-nowrap">
                              {/* Solo los productos "linea a medida" del
                                  catalogo, igual que en los presupuestos. El
                                  resto tiene precio de tarifa y aqui no se
                                  discute. */}
                              {puedeCambiarPrecio(item, products) ? (
                                <Input
                                  type="number" min="0" step="0.01"
                                  value={item.unit_price}
                                  onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                                  className="w-24 text-right ml-auto"
                                  title="Precio a medida: se puede cambiar en esta factura"
                                  data-testid={`linea-${index}-precio`}
                                />
                              ) : (
                                `${(item.unit_price ?? 0).toFixed(2)}€`
                              )}
                            </td>
                            <td className="p-2">
                              <Input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                className="w-20 text-center"
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                value={item.discount}
                                onChange={(e) => handleItemChange(index, 'discount', e.target.value)}
                                className="w-20 text-center"
                              />
                            </td>
                            <td className="p-2 text-right font-semibold">{item.subtotal.toFixed(2)}€</td>
                            <td className="p-2 text-right whitespace-nowrap">
                              {/* La cuota y su tipo. En el alta el tipo se
                                  puede tocar: vacio es el del cliente, 0 es
                                  sin impuesto. Lo normal es que venga puesto
                                  por el producto y no haya que tocarlo. */}
                              {(item.iva_amount ?? 0).toFixed(2)}€
                              {creandoFactura ? (
                                <span className="inline-flex items-center gap-0.5 ml-1">
                                  <Input
                                    type="number" min="0" max="100" step="any"
                                    value={item.tax_rate === null || item.tax_rate === undefined ? '' : item.tax_rate}
                                    onChange={(e) => handleItemChange(index, 'tax_rate', e.target.value)}
                                    placeholder={formatoTipo(tipoDeLinea({}, clienteElegido))}
                                    className="w-16 h-7 text-xs text-right px-1"
                                    title="% de impuesto de esta linea. Vacio = el del cliente"
                                    data-testid={`linea-${index}-impuesto`}
                                  />
                                  <span className="text-xs text-slate-500">%</span>
                                </span>
                              ) : (
                                <span className="text-xs text-slate-500 ml-1">
                                  ({formatoTipo(tipoDeLinea(item, clienteElegido))}%)
                                </span>
                              )}
                            </td>
                            <td className="p-2 text-right font-bold">{item.total.toFixed(2)}€</td>
                            <td className="p-2 text-center">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveItem(index)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Totals Summary */}
              {editForm.line_items.length > 0 && (
                <div className="bg-gray-50 border rounded-lg p-4">
                  <div className="space-y-2 text-sm max-w-xs ml-auto">
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span className="font-semibold">
                        {editForm.line_items.reduce((sum, item) => sum + item.subtotal, 0).toFixed(2)}€
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Impuesto:</span>
                      <span className="font-semibold">
                        {editForm.line_items.reduce((sum, item) => sum + item.iva_amount, 0).toFixed(2)}€
                      </span>
                    </div>
                    <div className="flex justify-between border-t pt-2 text-base font-bold">
                      <span>Total:</span>
                      <span className="text-[#9C0F17]">
                        {editForm.line_items.reduce((sum, item) => sum + item.total, 0).toFixed(2)}€
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Dos campos de texto porque son dos cosas. Las observaciones
                  salen impresas al pie de la factura; las notas no salen
                  nunca. Con uno solo, o el cliente lee lo que era para
                  dentro, o no lee lo que era para él. */}
              {/* Referencia por factura. Angel, 21-sep-2026: "en las facturas
                  manuales muestrame tambien el campo de Referencia, como las
                  repetitivas, puesto que lo necesito". Sale impresa en el PDF
                  y en la lista. Admite {mes}/{año}: en el alta las resuelve el
                  servidor con el mes del servicio; en una plantilla, el cron
                  cada mes. */}
              <div>
                <Label htmlFor="invoice_reference">Referencia por factura</Label>
                <Input
                  type="text"
                  id="invoice_reference"
                  value={editForm.invoice_reference}
                  onChange={(e) => setEditForm({ ...editForm, invoice_reference: e.target.value })}
                  placeholder="Ej: Pack Barra {mes} {año} - Local Centro"
                  className="mt-1"
                  data-testid="invoice-reference"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Sale en la factura, debajo del número. Admite <code className="bg-gray-100 px-1 rounded">{"{mes}"}</code> y <code className="bg-gray-100 px-1 rounded">{"{año}"}</code>.
                  {creandoFactura && llevaVariables(editForm.invoice_reference) && (
                    <> Saldrá: <em>{resolverVariables(editForm.invoice_reference, editForm.periodo)}</em></>
                  )}
                </p>
              </div>

              <div>
                <Label htmlFor="observaciones">Observaciones para el cliente</Label>
                <Textarea
                  id="observaciones"
                  value={editForm.observaciones}
                  onChange={(e) => setEditForm({ ...editForm, observaciones: e.target.value })}
                  placeholder="Sale impreso en la factura. Ej: Servicio del local de Chiclana."
                  rows={2}
                  data-testid="observaciones"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Se imprime en el PDF, debajo de los datos de pago.
                  {creandoFactura && (
                    <> También admite <code>{'{mes}'}</code> y <code>{'{año}'}</code>.</>
                  )}
                </p>
              </div>

              <div>
                <Label htmlFor="notes">Notas internas</Label>
                <Textarea
                  id="notes"
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  placeholder="Solo para nosotros. No sale en la factura."
                  rows={2}
                />
              </div>

              {/* Forma de Pago */}
              <div>
                <Label htmlFor="payment_method">Forma de Pago</Label>
                <Select
                  value={editForm.payment_method || ''}
                  onValueChange={(value) => setEditForm({ ...editForm, payment_method: value })}
                >
                  <SelectTrigger>
                    {/* En un alta se puede dejar sin elegir: el servidor pone
                        la que ese cliente ya paga (su última factura, o su
                        mandato SEPA). 503 de 554 facturas van por recibo, asi
                        que elegir "transferencia" a mano suele ser peor. */}
                    <SelectValue placeholder={creandoFactura
                      ? 'Elige cómo se cobra — obligatorio'
                      : 'Selecciona forma de pago'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sepa">🏦 Recibo Domiciliado (SEPA)</SelectItem>
                    <SelectItem value="transferencia">💸 Transferencia Bancaria</SelectItem>
                    {/* Lo paga el cliente por su cuenta. Sin esta opción, una
                        factura de alguien marcado así abría el desplegable en
                        blanco y había que elegirle otra cosa. */}
                    <SelectItem value="programado">📅 Pago programado por el cliente</SelectItem>
                    <SelectItem value="efectivo">💵 Efectivo</SelectItem>
                    <SelectItem value="tarjeta">💳 Tarjeta</SelectItem>
                    <SelectItem value="link">🔗 Link de Pago Online</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  {creandoFactura && clienteElegido?.payment_method
                    ? 'Viene puesta la de su ficha. Cámbiala si este pedido va de otra forma.'
                    : 'La forma de pago determina qué plantilla de email se usa al enviar la factura'}
                </p>
              </div>

              {/* Recurring Invoice Configuration.
                  En un alta no se ofrece: una factura suelta es lo que se
                  pidio, y una plantilla que se genera sola cada mes es otra
                  cosa que ademas no viaja en el POST. */}
              <div className={`border-t pt-4 ${creandoFactura ? 'hidden' : ''}`}>
                <div className="flex items-center gap-2 mb-4">
                  <input
                    type="checkbox"
                    id="is_recurring"
                    checked={editForm.is_recurring}
                    onChange={(e) => setEditForm({ ...editForm, is_recurring: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="is_recurring" className="font-semibold text-base">
                    🔄 Factura Repetitiva
                  </Label>
                </div>

                {/* Email plataforma (visible en plantillas Y en facturas generadas).
                    La Referencia estaba aqui dentro, y este bloque se oculta en
                    el alta: por eso una factura a mano no la tenia. Ahora vive
                    junto a las observaciones, fuera del bloque. */}
                <div className="grid grid-cols-1 gap-4 mb-4">
                  <div>
                    <Label htmlFor="platform_email">
                      Email de la plataforma
                    </Label>
                    <Input
                      type="email"
                      id="platform_email"
                      value={editForm.platform_email}
                      onChange={(e) => setEditForm({ ...editForm, platform_email: e.target.value })}
                      placeholder="ej: local@cliente.com"
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Email de la plataforma C10 asociada a la factura (distinto del email del cliente).
                    </p>
                  </div>
                </div>

                {editForm.is_recurring && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                    {/* Variables Info */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <p className="text-sm font-semibold text-amber-800 mb-1">💡 Variables disponibles en descripción:</p>
                      <p className="text-xs text-amber-700">
                        <code className="bg-amber-100 px-1 rounded">{"{mes}"}</code> → Nombre del mes (Enero, Febrero...)
                        <span className="mx-2">|</span>
                        <code className="bg-amber-100 px-1 rounded">{"{año}"}</code> → Año (2025, 2026...)
                        <span className="mx-2">|</span>
                        <code className="bg-amber-100 px-1 rounded">{"{mes_num}"}</code> → Número del mes (01, 02...)
                      </p>
                      <p className="text-xs text-amber-600 mt-1">Se reemplazan automáticamente al aprobar la factura.</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      {/* Start Date */}
                      <div>
                        <Label htmlFor="start_date">Fecha de Inicio *</Label>
                        <Input
                          type="date"
                          id="start_date"
                          value={editForm.start_date}
                          onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })}
                          className="mt-1"
                        />
                        <p className="text-xs text-gray-500 mt-1">Primera emisión de la factura</p>
                      </div>

                      {/* End Date */}
                      <div>
                        <Label htmlFor="end_date">Fecha de Fin (opcional)</Label>
                        <Input
                          type="date"
                          id="end_date"
                          value={editForm.end_date}
                          onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })}
                          className="mt-1"
                        />
                        <p className="text-xs text-gray-500 mt-1">Dejar vacío = sin límite</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      {/* Frequency */}
                      <div>
                        <Label htmlFor="recurrence_frequency">Cadencia *</Label>
                        <Select
                          value={editForm.recurrence_frequency}
                          onValueChange={(value) => setEditForm({ 
                            ...editForm, 
                            recurrence_frequency: value,
                            recurrence_interval: 1 // Reset interval when changing frequency
                          })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Selecciona cadencia" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="mensual">📅 Mensual</SelectItem>
                            <SelectItem value="semanal">📆 Semanal</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Interval */}
                      <div>
                        <Label htmlFor="recurrence_interval">
                          Cada {editForm.recurrence_frequency === 'semanal' ? '(1-3 semanas)' : '(1-12 meses)'} *
                        </Label>
                        <Input
                          type="number"
                          id="recurrence_interval"
                          value={editForm.recurrence_interval}
                          onChange={(e) => setEditForm({ ...editForm, recurrence_interval: parseInt(e.target.value) || 1 })}
                          min="1"
                          max={editForm.recurrence_frequency === 'semanal' ? 3 : 12}
                          className="mt-1"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {editForm.recurrence_frequency === 'mensual' && editForm.recurrence_interval === 1 && 'Mensual'}
                          {editForm.recurrence_frequency === 'mensual' && editForm.recurrence_interval === 3 && 'Trimestral'}
                          {editForm.recurrence_frequency === 'mensual' && editForm.recurrence_interval === 6 && 'Semestral'}
                          {editForm.recurrence_frequency === 'mensual' && editForm.recurrence_interval === 12 && 'Anual'}
                          {editForm.recurrence_frequency === 'semanal' && `Cada ${editForm.recurrence_interval} semana(s)`}
                        </p>
                      </div>
                    </div>

                    {/* Validation Warning */}
                    {(!editForm.start_date || !editForm.recurrence_frequency) && (
                      <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
                        ⚠️ Faltan datos obligatorios: La factura se marcará en ROJO hasta completar fecha de inicio y cadencia.
                      </div>
                    )}
                    {editForm.status === 'draft' && editForm.start_date && editForm.recurrence_frequency && (
                      <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-700">
                        ℹ️ <strong>Recuerda:</strong> Una vez completes la configuración, debes <strong>aceptar esta factura</strong> (arriba) para que comience a generar facturas automáticamente.
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  onClick={handleSaveEdit}
                  // Solo frena el alta, donde guardar es emitir. Un borrador se
                  // guarda igual: avisa, y el candado lo pone el servidor al emitir.
                  disabled={creandoFactura && Boolean(motivoNoEmitir)}
                  title={(creandoFactura && motivoNoEmitir) || undefined}
                  className="bg-[#9C0F17] hover:bg-[#7a0c12]"
                >
                  {creandoFactura ? 'Emitir factura' : 'Guardar Cambios'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* El link de pago recién generado.
            Va en un diálogo y no en un aviso que se va solo porque hay que
            COPIARLO: es lo único que le sirve al cliente para pagar. */}
        <Dialog open={Boolean(linkGenerado)} onOpenChange={() => setLinkGenerado(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Link de pago de {linkGenerado?.numero}</DialogTitle>
              <DialogDescription>
                Mándaselo al cliente. Caduca solo; si pasa, se genera otro con el
                mismo botón.
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-lg border bg-slate-50 p-3">
              <code className="text-xs break-all">{linkGenerado?.url}</code>
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setLinkGenerado(null)}>
                Cerrar
              </Button>
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(linkGenerado?.url || '');
                  toast.success('Link copiado');
                }}
                className="bg-[#9C0F17] hover:bg-[#7a0c12] text-white"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copiar
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Confirmar la emision.
            No es un "¿seguro?" de tramite: es la ultima pantalla antes de que
            la factura tenga numero. A partir de ahi lo unico que arregla un
            error es una rectificativa, asi que aqui se enseña a quien se le
            emite y por cuanto, que es lo que de verdad se puede haber
            tecleado mal. */}
        <Dialog open={confirmarEmision} onOpenChange={setConfirmarEmision}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>¿Emitir esta factura?</DialogTitle>
              <DialogDescription>
                Cogerá número de la serie y quedará emitida. No se puede deshacer:
                para corregirla habría que hacer una rectificativa.
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-lg border bg-slate-50 p-4 space-y-2 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-slate-500">Cliente</span>
                <span className="font-medium text-right">
                  {clienteElegido?.fiscal_name || clienteElegido?.name || '—'}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-slate-500">Conceptos</span>
                <span className="font-medium">{editForm.line_items.length}</span>
              </div>
              <div className="flex justify-between gap-4 border-t pt-2">
                <span className="text-slate-500">Total</span>
                <span className="font-bold text-base">
                  {totalDelFormulario().toFixed(2)} €
                </span>
              </div>
              <p className="text-xs text-slate-500 pt-1">
                El impuesto lo pone el servidor según la ficha del cliente, así que
                este total puede cambiar si está fuera del IVA.
              </p>
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setConfirmarEmision(false)}
                      disabled={emitiendo}>
                Volver
              </Button>
              <Button onClick={emitirFacturaNueva} disabled={emitiendo}
                      className="bg-[#9C0F17] hover:bg-[#7a0c12] text-white">
                {emitiendo ? 'Emitiendo...' : 'Sí, emitir'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Billing Alert Dialog */}
        <Dialog open={billingAlertOpen} onOpenChange={(open) => {
          setBillingAlertOpen(open);
          if (!open) {
            // Open edit dialog after closing alert
            setEditDialogOpen(true);
          }
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-orange-600">
                <AlertCircle className="w-5 h-5" />
                Nota de Facturación
              </DialogTitle>
              <DialogDescription>
                Este cliente tiene una nota importante sobre facturación
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-sm text-gray-800 whitespace-pre-wrap">
                  {billingAlertNote}
                </p>
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={() => {
                  setBillingAlertOpen(false);
                  setEditDialogOpen(true);
                }}
                className="bg-[#9C0F17] hover:bg-[#7a0c12] text-white"
              >
                Entendido
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* PDF Import Dialog */}
        <Dialog open={pdfDialogOpen} onOpenChange={setPdfDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Importar Factura desde PDF</DialogTitle>
              <DialogDescription>
                Sube un PDF de factura para extraer automáticamente los datos del cliente y productos.<br/>
                <span className="text-purple-600 font-medium">💡 Los PDFs con múltiples páginas se procesarán como facturas separadas (máx. 30)</span>
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* File Upload */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <FileText className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                <div className="text-sm text-gray-600 mb-2">
                  {pdfFile ? (
                    <span className="text-green-600 font-medium">✓ {pdfFile.name}</span>
                  ) : (
                    <>Selecciona un archivo PDF de factura</>
                  )}
                </div>
                <Input
                  id="pdf-file"
                  type="file"
                  accept=".pdf"
                  onChange={handlePdfFileChange}
                  className="hidden"
                />
                <label htmlFor="pdf-file">
                  <Button type="button" variant="outline" className="mt-2" asChild>
                    <span>{processingPdf ? 'Procesando...' : 'Seleccionar PDF'}</span>
                  </Button>
                </label>
              </div>

              {/* Preview Data */}
              {pdfPreviewData && (
                <div className="space-y-4">
                  {/* Check if multiple invoices or single */}
                  {pdfPreviewData.total_pages > 1 ? (
                    /* Multiple Invoices Preview */
                    <div className="space-y-4">
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <h4 className="font-semibold text-sm text-purple-900 mb-2">
                          📄 Múltiples Facturas Detectadas
                        </h4>
                        <div className="text-sm space-y-2">
                          <p>Total páginas: <strong>{pdfPreviewData.total_pages}</strong></p>
                          <p>Facturas procesadas: <strong className="text-green-600">{pdfPreviewData.invoices_processed}</strong></p>
                          {pdfPreviewData.invoices_failed > 0 && (
                            <p>Facturas con error: <strong className="text-red-600">{pdfPreviewData.invoices_failed}</strong></p>
                          )}
                        </div>
                      </div>

                      {/* List of invoices */}
                      <div className="max-h-96 overflow-y-auto space-y-3">
                        {pdfPreviewData.invoices.map((invoice, idx) => (
                          <div key={idx} className={`border rounded-lg p-3 ${invoice.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="font-semibold text-sm">
                                {invoice.success ? '✓' : '✗'} Página {invoice.page}
                              </h5>
                              {invoice.success && invoice.summary && (
                                <span className="text-sm font-bold">{invoice.summary.total.toFixed(2)} €</span>
                              )}
                            </div>
                            {invoice.success ? (
                              <div className="text-xs space-y-1">
                                <p><strong>Cliente:</strong> {invoice.client.name} ({invoice.client.tax_id})</p>
                                <p><strong>Productos:</strong> {invoice.products.length} items</p>
                                <p><strong>Subtotal:</strong> {invoice.summary.subtotal.toFixed(2)} € + IVA</p>
                              </div>
                            ) : (
                              <p className="text-xs text-red-600">{invoice.error}</p>
                            )}
                          </div>
                        ))}
                      </div>

                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <p className="text-xs text-yellow-800">
                          ℹ️ Se crearán <strong>{pdfPreviewData.invoices_processed} facturas</strong> en estado <strong>BORRADOR</strong> configuradas como <strong>RECURRENTES MENSUALES</strong>
                        </p>
                      </div>
                    </div>
                  ) : (
                    /* Single Invoice Preview */
                    <div className="space-y-4">
                      {pdfPreviewData.client && (
                        <>
                          {/* Client Info */}
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <h4 className="font-semibold text-sm text-blue-900 mb-2">✓ Cliente Identificado</h4>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <span className="font-medium">Nombre:</span> {pdfPreviewData.client.name}
                              </div>
                              <div>
                                <span className="font-medium">NIF/CIF:</span> {pdfPreviewData.client.tax_id}
                              </div>
                              <div>
                                <span className="font-medium">Email:</span> {pdfPreviewData.client.email}
                              </div>
                              {pdfPreviewData.client.fiscal_name && (
                                <div>
                                  <span className="font-medium">Nombre Fiscal:</span> {pdfPreviewData.client.fiscal_name}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Products Table */}
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <h4 className="font-semibold text-sm text-green-900 mb-3">✓ Productos Encontrados</h4>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead className="bg-green-100">
                                  <tr>
                                    <th className="p-2 text-left">Referencia</th>
                                    <th className="p-2 text-left">Producto</th>
                                    <th className="p-2 text-right">Cantidad</th>
                                    <th className="p-2 text-right">Precio Unit.</th>
                                    <th className="p-2 text-right">Subtotal</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {pdfPreviewData.products.map((product, idx) => (
                                    <tr key={idx} className="border-t border-green-200">
                                      <td className="p-2 font-mono">{product.ref}</td>
                                      <td className="p-2">
                                        {product.name}
                                        {product.price_warning && (
                                          <div className="text-red-600 text-xs mt-1">{product.price_warning}</div>
                                        )}
                                      </td>
                                      <td className="p-2 text-right">{product.quantity}</td>
                                      <td className="p-2 text-right">{product.price.toFixed(2)} €</td>
                                      <td className="p-2 text-right">{(product.quantity * product.price).toFixed(2)} €</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Summary */}
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <h4 className="font-semibold text-sm text-gray-900 mb-2">Resumen</h4>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span>Subtotal:</span>
                                <span>{pdfPreviewData.summary.subtotal.toFixed(2)} €</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Impuesto:</span>
                                <span>{pdfPreviewData.summary.iva_amount.toFixed(2)} €</span>
                              </div>
                              <div className="flex justify-between font-bold text-base border-t pt-2">
                                <span>Total:</span>
                                <span>{pdfPreviewData.summary.total.toFixed(2)} €</span>
                              </div>
                            </div>
                          </div>

                          {/* Info Box */}
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                            <p className="text-xs text-yellow-800">
                              ℹ️ Se creará una factura en estado <strong>BORRADOR</strong> configurada como <strong>RECURRENTE MENSUAL</strong>
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setPdfDialogOpen(false);
                    setPdfFile(null);
                    setPdfPreviewData(null);
                  }}
                  disabled={creatingInvoice}
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  onClick={handleCreateInvoiceFromPdf}
                  disabled={!pdfPreviewData || creatingInvoice}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {creatingInvoice ? (
                    <>Creando...</>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Crear Factura Borrador
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Excel Recurring Configuration Modal */}
        <Dialog open={excelConfigDialogOpen} onOpenChange={setExcelConfigDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>⚙️ Configuración de Facturas Repetitivas</DialogTitle>
              <p className="text-sm text-gray-600 mt-2">
                Todas las facturas importadas desde Excel se crearán como <strong>facturas repetitivas aceptadas</strong> con la configuración que definas a continuación.
              </p>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Start Date */}
                <div>
                  <Label htmlFor="excel_start_date">Fecha de Inicio *</Label>
                  <Input
                    type="date"
                    id="excel_start_date"
                    value={excelRecurringConfig.start_date}
                    onChange={(e) => setExcelRecurringConfig({ ...excelRecurringConfig, start_date: e.target.value })}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Primera emisión de facturas</p>
                </div>

                {/* End Date */}
                <div>
                  <Label htmlFor="excel_end_date">Fecha de Fin (opcional)</Label>
                  <Input
                    type="date"
                    id="excel_end_date"
                    value={excelRecurringConfig.end_date}
                    onChange={(e) => setExcelRecurringConfig({ ...excelRecurringConfig, end_date: e.target.value })}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Dejar vacío = sin límite</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Frequency */}
                <div>
                  <Label htmlFor="excel_frequency">Cadencia *</Label>
                  <Select
                    value={excelRecurringConfig.recurrence_frequency}
                    onValueChange={(value) => setExcelRecurringConfig({ 
                      ...excelRecurringConfig, 
                      recurrence_frequency: value,
                      recurrence_interval: 1 
                    })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mensual">📅 Mensual</SelectItem>
                      <SelectItem value="semanal">📆 Semanal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Interval */}
                <div>
                  <Label htmlFor="excel_interval">
                    Cada {excelRecurringConfig.recurrence_frequency === 'semanal' ? '(1-3 semanas)' : '(1-12 meses)'} *
                  </Label>
                  <Input
                    type="number"
                    id="excel_interval"
                    value={excelRecurringConfig.recurrence_interval}
                    onChange={(e) => setExcelRecurringConfig({ ...excelRecurringConfig, recurrence_interval: parseInt(e.target.value) || 1 })}
                    min="1"
                    max={excelRecurringConfig.recurrence_frequency === 'semanal' ? 3 : 12}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {excelRecurringConfig.recurrence_frequency === 'mensual' && excelRecurringConfig.recurrence_interval === 1 && 'Mensual'}
                    {excelRecurringConfig.recurrence_frequency === 'mensual' && excelRecurringConfig.recurrence_interval === 3 && 'Trimestral'}
                    {excelRecurringConfig.recurrence_frequency === 'mensual' && excelRecurringConfig.recurrence_interval === 6 && 'Semestral'}
                    {excelRecurringConfig.recurrence_frequency === 'mensual' && excelRecurringConfig.recurrence_interval === 12 && 'Anual'}
                    {excelRecurringConfig.recurrence_frequency === 'semanal' && `Cada ${excelRecurringConfig.recurrence_interval} semana(s)`}
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded p-4 mt-4">
                <p className="text-sm text-blue-800">
                  <strong>ℹ️ Información:</strong><br/>
                  • Las facturas se crearán como <strong>aceptadas</strong> (no borrador)<br/>
                  • Se aplicará esta configuración a <strong>todas</strong> las facturas del Excel<br/>
                  • Podrás editar individualmente cada factura después si es necesario
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setExcelConfigDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  if (!excelRecurringConfig.start_date) {
                    toast.error('La fecha de inicio es obligatoria');
                    return;
                  }
                  setExcelConfigDialogOpen(false);
                  setExcelDialogOpen(true);
                }}
                className="bg-green-600 hover:bg-green-700"
              >
                Continuar →
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Excel Import Modal */}
        <Dialog open={excelDialogOpen} onOpenChange={setExcelDialogOpen}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Importar Facturas desde Excel</DialogTitle>
              <DialogDescription>
                Sube un archivo Excel con facturas repetitivas mensuales. El sistema validará clientes, productos y creará facturas en borrador.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* File Upload */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <Upload className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                <div className="text-sm text-gray-600 mb-2">
                  {excelFile ? (
                    <span className="text-green-600 font-medium">✓ {excelFile.name}</span>
                  ) : (
                    <>Selecciona un archivo Excel (.xlsx o .xls)</>
                  )}
                </div>
                <Input
                  id="excel-file"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleExcelFileChange}
                  className="hidden"
                />
                <label htmlFor="excel-file">
                  <Button type="button" variant="outline" className="mt-2" asChild>
                    <span>{processingExcel ? 'Procesando...' : 'Seleccionar Excel'}</span>
                  </Button>
                </label>
              </div>

              {/* Preview Data */}
              {excelPreviewData && (
                <div className="space-y-4">
                  {/* Stats Summary */}
                  <div className="grid grid-cols-3 gap-4">
                    <Card>
                      <CardContent className="pt-6">
                        <div className="text-center">
                          <div className="text-3xl font-bold text-blue-600">{excelPreviewData.stats.total_invoices}</div>
                          <div className="text-sm text-gray-600 mt-1">Facturas Detectadas</div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <div className="text-center">
                          <div className="text-3xl font-bold text-green-600">{excelPreviewData.stats.valid_invoices}</div>
                          <div className="text-sm text-gray-600 mt-1">Válidas</div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <div className="text-center">
                          <div className="text-3xl font-bold text-red-600">{excelPreviewData.stats.invalid_invoices}</div>
                          <div className="text-sm text-gray-600 mt-1">Con Errores</div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Errors Summary */}
                  {excelPreviewData.errors && excelPreviewData.errors.length > 0 && (
                    <Card className="border-red-200 bg-red-50">
                      <CardHeader>
                        <CardTitle className="text-sm text-red-900">⚠️ Errores Bloqueantes</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {excelPreviewData.errors.map((error, idx) => (
                            <div key={idx} className="text-sm text-red-800 bg-white p-2 rounded border border-red-200">
                              {typeof error === 'string' ? error : (
                                <div>
                                  <strong>Factura {error.invoice_code}:</strong> {error.error}
                                  {error.rows && ` (Filas: ${error.rows.join(', ')})`}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Invoices Preview */}
                  <div className="border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-3 text-left font-medium text-gray-700">Estado</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-700">Código</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-700">Cliente</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-700">Fecha</th>
                          <th className="px-4 py-3 text-right font-medium text-gray-700">Líneas</th>
                          <th className="px-4 py-3 text-right font-medium text-gray-700">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {excelPreviewData.invoices_preview.map((invoice, idx) => (
                          <tr key={idx} className={invoice.valid ? 'bg-white hover:bg-gray-50' : 'bg-red-50'}>
                            <td className="px-4 py-3">
                              {invoice.valid ? (
                                <Badge className="bg-green-100 text-green-800">✓ Válida</Badge>
                              ) : (
                                <Badge variant="destructive">✗ Error</Badge>
                              )}
                            </td>
                            <td className="px-4 py-3 font-mono text-xs">{invoice.invoice_code}</td>
                            <td className="px-4 py-3">
                              {invoice.customer ? (
                                <div>
                                  <div className="font-medium">{invoice.customer.name}</div>
                                  <div className="text-xs text-gray-500">{invoice.customer.tax_id}</div>
                                </div>
                              ) : (
                                <span className="text-red-600 text-xs">CIF: {invoice.cif || 'N/A'}</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-xs">
                              {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString('es-ES') : 'N/A'}
                            </td>
                            <td className="px-4 py-3 text-right">{invoice.lines_count}</td>
                            <td className="px-4 py-3 text-right font-medium">{invoice.invoice_total?.toFixed(2)} €</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Detailed Errors per Invoice */}
                  {excelPreviewData.invoices_preview.some(inv => !inv.valid && inv.errors?.length > 0) && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm">Detalle de Errores por Factura</CardTitle>
                      </CardHeader>
                      <CardContent className="max-h-60 overflow-y-auto">
                        <div className="space-y-3">
                          {excelPreviewData.invoices_preview
                            .filter(inv => !inv.valid && inv.errors?.length > 0)
                            .map((invoice, idx) => (
                              <div key={idx} className="bg-red-50 border border-red-200 rounded p-3">
                                <div className="font-medium text-sm text-red-900 mb-2">
                                  {invoice.invoice_code}
                                </div>
                                <ul className="list-disc list-inside space-y-1">
                                  {invoice.errors.map((error, eidx) => (
                                    <li key={eidx} className="text-xs text-red-800">{error}</li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Warnings */}
                  {excelPreviewData.invoices_preview.some(inv => inv.warnings?.length > 0) && (
                    <Card className="border-yellow-200 bg-yellow-50">
                      <CardHeader>
                        <CardTitle className="text-sm text-yellow-900">⚠️ Avisos (No bloqueantes)</CardTitle>
                      </CardHeader>
                      <CardContent className="max-h-40 overflow-y-auto">
                        <div className="space-y-2">
                          {excelPreviewData.invoices_preview
                            .filter(inv => inv.warnings?.length > 0)
                            .map((invoice, idx) => (
                              <div key={idx} className="bg-white p-2 rounded border border-yellow-200">
                                <div className="font-medium text-xs text-yellow-900 mb-1">{invoice.invoice_code}</div>
                                <ul className="list-disc list-inside space-y-0.5">
                                  {invoice.warnings.map((warning, widx) => (
                                    <li key={widx} className="text-xs text-yellow-800">{warning}</li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {/* Create Button */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setExcelDialogOpen(false);
                    setExcelFile(null);
                    setExcelPreviewData(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleCreateInvoicesFromExcel}
                  disabled={!excelPreviewData || excelPreviewData.stats.valid_invoices === 0 || creatingFromExcel}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {creatingFromExcel ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Creando...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Crear {excelPreviewData?.stats.valid_invoices || 0} Facturas Borrador
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Import repetitivas (XLSX Paygold/Xero) */}
        <Dialog open={recurringDialogOpen} onOpenChange={(open) => !open && closeRecurringDialog()}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Importar plantillas repetitivas</DialogTitle>
              <DialogDescription>
                {recurringImporting
                  ? 'Analizando el XLSX (dry-run, sin escribir nada)...'
                  : recurringResult
                    ? 'Resultado del import'
                    : recurringPreview
                      ? 'Preview del import — revisa los números antes de aplicar'
                      : ''}
              </DialogDescription>
            </DialogHeader>

            {recurringImporting && (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-8 h-8 animate-spin text-[#13B5EA]" />
              </div>
            )}

            {!recurringImporting && (recurringResult || recurringPreview) && (() => {
              const r = recurringResult || recurringPreview;
              if (r.error) {
                return (
                  <div className="p-4 bg-red-50 border border-red-200 rounded text-red-800">
                    {r.error}
                  </div>
                );
              }
              const isApplied = !!recurringResult;
              return (
                <div className="space-y-3 text-sm">
                  {/* Altas, cambios y bajas: el Excel es la fuente de verdad, asi que
                      un import puede no crear nada y aun asi cambiar muchisimo. */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-green-50 rounded border border-green-200">
                      <div className="text-2xl font-bold text-green-700">{r.templates_created}</div>
                      <div className="text-xs text-green-800">Plantillas {isApplied ? 'creadas' : 'a crear'}</div>
                    </div>
                    <div className="p-3 bg-amber-50 rounded border border-amber-200">
                      <div className="text-2xl font-bold text-amber-700">{r.templates_updated ?? 0}</div>
                      <div className="text-xs text-amber-800">Plantillas {isApplied ? 'actualizadas' : 'a actualizar'}</div>
                    </div>
                    <div className="p-3 bg-red-50 rounded border border-red-200">
                      <div className="text-2xl font-bold text-red-700">{r.templates_deleted ?? 0}</div>
                      <div className="text-xs text-red-800">Bajas {isApplied ? 'aplicadas' : 'a dar de baja'}</div>
                    </div>
                    <div className="p-3 bg-slate-50 rounded border border-slate-200">
                      <div className="text-2xl font-bold text-slate-700">{r.templates_unchanged ?? 0}</div>
                      <div className="text-xs text-slate-800">Sin cambios</div>
                    </div>
                    <div className="p-3 bg-blue-50 rounded border border-blue-200">
                      <div className="text-2xl font-bold text-blue-700">{r.lines_imported}</div>
                      <div className="text-xs text-blue-800">Líneas {isApplied ? 'importadas' : 'a importar'}</div>
                    </div>
                    <div className="p-3 bg-orange-50 rounded border border-orange-200">
                      <div className="text-2xl font-bold text-orange-700">{r.customers_not_found}</div>
                      <div className="text-xs text-orange-800">Clientes sin match</div>
                    </div>
                  </div>

                  {r.warnings?.length > 0 && (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-300 rounded">
                      <div className="text-xs font-semibold text-yellow-900 mb-1">⚠️ Avisos</div>
                      {r.warnings.map((w, i) => (
                        <div key={i} className="text-xs text-yellow-900">{w}</div>
                      ))}
                    </div>
                  )}

                  {r.deleted_details?.length > 0 && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                      <div className="text-xs font-semibold text-red-900 mb-1">
                        Plantillas que ya no están en el Excel ({r.deleted_details.length}):
                      </div>
                      <div className="max-h-40 overflow-y-auto">
                        {r.deleted_details.map((d, i) => (
                          <div key={i} className="text-xs text-red-800">
                            {d.customer} — {d.total} € — próxima: {String(d.next_creation_date || '').slice(0, 10)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {r.template_changes?.length > 0 && (
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded">
                      <div className="text-xs font-semibold text-amber-900 mb-1">
                        Qué cambia en cada plantilla (primeras {r.template_changes.length}):
                      </div>
                      <div className="max-h-56 overflow-y-auto space-y-2">
                        {r.template_changes.map((c, i) => (
                          <div key={i} className="text-xs">
                            <div className="font-semibold text-amber-900">{c.customer}</div>
                            {Object.entries(c.changes || {}).map(([campo, d]) => (
                              <div key={campo} className="text-amber-800 pl-2">
                                {campo}: <span className="line-through opacity-70">{String(d.old)}</span>
                                {' → '}<strong>{String(d.new)}</strong>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="text-xs text-gray-700 space-y-1 pt-2">
                    <div>📊 Plantillas en XLSX: <strong>{r.total_templates_in_xlsx}</strong></div>
                    <div>🔗 Clientes matcheados (por NIF/email): <strong>{r.customers_matched}</strong></div>
                    {r.templates_skipped_existing > 0 && (
                      <div>⏭️ Plantillas ya existentes (skip): <strong>{r.templates_skipped_existing}</strong></div>
                    )}
                    {r.customers_email_updated > 0 && (
                      <div>📧 Emails de cliente actualizados: <strong>{r.customers_email_updated}</strong></div>
                    )}
                    {r.customers_phone_updated > 0 && (
                      <div>📞 Teléfonos de cliente actualizados: <strong>{r.customers_phone_updated}</strong></div>
                    )}
                    {r.customers_platform_email_backfilled > 0 && (
                      <div>✉️ Platform emails rellenados en customers: <strong>{r.customers_platform_email_backfilled}</strong></div>
                    )}
                    {r.products_shell_created > 0 && (
                      <div>📦 Productos shell creados (SKUs nuevos): <strong>{r.products_shell_created}</strong></div>
                    )}
                    {r.lines_without_sku > 0 && (
                      <div>⚠️ Líneas sin SKU (usan "SIN-SKU" como ref): <strong>{r.lines_without_sku}</strong></div>
                    )}
                  </div>

                  {r.unmatched_details?.length > 0 && (
                    <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded">
                      <div className="text-xs font-semibold text-orange-900 mb-1">
                        Clientes sin match (primeros {r.unmatched_details.length}):
                      </div>
                      <ul className="text-xs text-orange-800 list-disc list-inside space-y-1 max-h-32 overflow-y-auto">
                        {r.unmatched_details.map((u, i) => (
                          <li key={i}>
                            {u.cliente} — NIF: {u.nif || '(vacío)'} — Email: {u.email || '(vacío)'}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {r.errors?.length > 0 && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                      <div className="text-xs font-semibold text-red-900 mb-1">
                        {r.errors.length} errores (primeros 10):
                      </div>
                      <ul className="text-xs text-red-800 list-disc list-inside space-y-1 max-h-32 overflow-y-auto">
                        {r.errors.slice(0, 10).map((e, i) => <li key={i}>{e}</li>)}
                      </ul>
                    </div>
                  )}

                  {!isApplied && (
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded text-amber-900 text-xs">
                      ⚠️ Esto es solo una <strong>simulación (dry-run)</strong>. No se ha escrito nada en la base de datos. Pulsa <strong>"Aplicar import"</strong> para crear las plantillas de verdad.
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={closeRecurringDialog}
                disabled={recurringImporting || recurringApplying}
              >
                Cerrar
              </Button>
              {recurringPreview && !recurringPreview.error && !recurringResult && (
                <Button
                  onClick={handleRecurringApply}
                  disabled={recurringApplying}
                  className="bg-[#13B5EA] hover:bg-[#0EA1D3] text-white"
                >
                  {recurringApplying ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Aplicando...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Aplicar import ({recurringPreview.templates_created} plantillas)
                    </>
                  )}
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Rectificativas. Vive fuera de la tabla porque necesita la factura
            entera con sus lineas, no la fila. */}
        <DialogoEnviarFactura
          invoice={enviando}
          open={Boolean(enviando)}
          onOpenChange={(abierto) => { if (!abierto) setEnviando(null); }}
          onEnviado={() => { setEnviando(null); fetchInvoices(); }}
        />

        <DialogoRectificativa
          invoice={rectificando}
          open={Boolean(rectificando)}
          onOpenChange={(abierto) => { if (!abierto) setRectificando(null); }}
          onHecho={() => { setRectificando(null); fetchInvoices(); }}
        />

        <DialogoCobro
          invoice={cobrando}
          open={Boolean(cobrando)}
          onOpenChange={(abierto) => { if (!abierto) setCobrando(null); }}
          onHecho={() => { setCobrando(null); fetchInvoices(); }}
        />

        {/* Recibo devuelto por el banco. Se marca a mano hasta que llegue
            por API desde la aplicación que lleva la recuperación. */}
        <Dialog open={Boolean(devolviendoRecibo)}
                onOpenChange={(abierto) => { if (!abierto) setDevolviendoRecibo(null); }}>
          <DialogContent className="max-w-md" data-testid="dialogo-recibo-devuelto">
            <DialogHeader>
              <DialogTitle>Recibo devuelto</DialogTitle>
              <DialogDescription>
                {devolviendoRecibo?.invoice_number} · {devolviendoRecibo?.customer_name}
                {' · '}{(devolviendoRecibo?.total ?? 0).toFixed(2)} €
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              La factura dejará de constar pagada y volverá a estar pendiente de cobro.
              No vuelve a la cola de remesas SEPA: se cobra por el otro camino.
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="fecha-devolucion">Fecha de la devolución *</Label>
              <Input
                id="fecha-devolucion" type="date"
                value={fechaDevolucion}
                onChange={(e) => setFechaDevolucion(e.target.value)}
                data-testid="fecha-devolucion"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="motivo-devolucion-recibo">Motivo (opcional)</Label>
              <Input
                id="motivo-devolucion-recibo" type="text"
                value={motivoDevolucion}
                onChange={(e) => setMotivoDevolucion(e.target.value)}
                placeholder="Lo que diga el banco. Ej: sin fondos"
                data-testid="motivo-devolucion-recibo"
              />
            </div>

            {devolviendoRecibo?.recibo_devuelto && (
              <p className="text-xs text-amber-700">
                Ojo: a esta factura ya se le devolvió el recibo
                {devolviendoRecibo.recibos_devueltos > 1
                  ? ` ${devolviendoRecibo.recibos_devueltos} veces` : ' una vez'} antes.
              </p>
            )}

            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setDevolviendoRecibo(null)}
                      disabled={marcandoDevuelto}>
                Cancelar
              </Button>
              <Button onClick={marcarReciboDevuelto}
                      disabled={!fechaDevolucion || marcandoDevuelto}
                      className="bg-[#9C0F17] hover:bg-[#7a0c12] text-white"
                      data-testid="confirmar-recibo-devuelto">
                {marcandoDevuelto ? 'Marcando...' : 'Marcar devuelto'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* No recibe factura: no hay ninguna de la que partir. La original se
            emitió con la aplicación anterior. */}
        <DialogoDevolucionManual
          open={devolviendoAMano}
          onOpenChange={setDevolviendoAMano}
          onHecho={() => { setDevolviendoAMano(false); verLoRecienHecho(); }}
        />
      </div>
    </Layout>
  );
}
